/**
 * Staffing Command Center - Main Application
 * Built and maintained by Angel Carreon (acarreom)
 * Copyright © 2025. All rights reserved.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import { 
  subscribeToStaff, 
  addStaff, 
  updateStaff, 
  deleteStaff, 
  // initializeDefaultStaff, // No longer used 
  testFirebaseConnection,
  subscribeToAssignedStaff,
  clearAllAssignedStaff,
  deletePlan
} from './services/staffService';
import clockInService from './services/clockInService';
import { doc, getDoc, setDoc, getDocs, collection, onSnapshot, serverTimestamp, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from './firebase';
import { ticketService } from './supabase';
import { validateAdminCredentials } from './utils/security';


function App({ isDevMode, onExitDevMode, isAuthenticated }) {




  // Simple FCLM redirect function using direct login
  const handleFCLMRedirect = (staffLogin) => {
    try {
      // Validate staffLogin parameter
      if (!staffLogin || typeof staffLogin !== 'string') {
        console.error('Invalid staffLogin parameter:', staffLogin);
        alert('Error: Invalid staff login information. Please try again.');
        return;
      }
      
      // Construct FCLM URL using the exact format specified: fclm-portal.amazon.com/employee/timeDetails?employeeId=LOGIN&warehouseId=BDL3
      const fclmUrl = `https://fclm-portal.amazon.com/employee/timeDetails?employeeId=${staffLogin}&warehouseId=BDL3`;
      
      console.log(`Attempting to open FCLM for ${staffLogin} at: ${fclmUrl}`);
      
      // Simple approach: just use window.open once
      console.log('Opening FCLM in new tab...');
      window.open(fclmUrl, '_blank', 'noopener,noreferrer');
      console.log('FCLM redirect executed');
      
    } catch (error) {
      console.error('Error opening FCLM for staff:', staffLogin, error);
      // Fallback to main FCLM portal if any error occurs
      window.open('https://fclm-portal.amazon.com/?warehouseId=BDL3', '_blank', 'noopener,noreferrer');
    }
  };

  // CSV Import Functions (Admin Only)
  const parseCsvFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvText = e.target.result;
          const lines = csvText.split('\n').map(line => line.trim()).filter(line => line);
          
          if (lines.length === 0) {
            reject(new Error('CSV file is empty'));
            return;
          }

          // Parse header row
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
          
          // Validate required columns
          const requiredColumns = ['login', 'employeeid'];
          const optionalColumns = ['role', 'status'];
          const missingColumns = requiredColumns.filter(col => !headers.includes(col));
          
          if (missingColumns.length > 0) {
            reject(new Error(`Missing required columns: ${missingColumns.join(', ')}. Expected columns: login, employeeid. Optional columns: role, status`));
            return;
          }

          // Parse data rows
          const employeeData = [];
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim());
            
            if (values.length !== headers.length) {
              console.warn(`Row ${i + 1} has ${values.length} columns, expected ${headers.length}. Skipping.`);
              continue;
            }

            const rowData = {};
            headers.forEach((header, index) => {
              rowData[header] = values[index];
            });

            // Validate required fields
            if (!rowData.login || !rowData.employeeid) {
              console.warn(`Row ${i + 1} missing login or employeeid. Skipping.`);
              continue;
            }

            // Clean and validate employee ID (should be numeric)
            const employeeId = rowData.employeeid.replace(/[^0-9]/g, '');
            if (!employeeId || employeeId.length < 6) {
              console.warn(`Row ${i + 1} has invalid employee ID: ${rowData.employeeid}. Skipping.`);
              continue;
            }

            // Parse optional staff fields
            const role = rowData.role || ''; // Optional role
            const status = rowData.status ? rowData.status.toLowerCase() : 'red'; // Default to 'red' if not specified
            
            // Validate status if provided
            const validStatuses = ['red', 'orange', 'green', 'light-green'];
            const finalStatus = validStatuses.includes(status) ? status : 'red';
            
            if (rowData.status && !validStatuses.includes(status)) {
              console.warn(`Row ${i + 1} has invalid status: ${rowData.status}. Using 'red' as default.`);
            }

            employeeData.push({
              login: rowData.login.toLowerCase(),
              employeeId: employeeId,
              role: role,
              status: finalStatus,
              originalRow: i + 1
            });
          }

          if (employeeData.length === 0) {
            reject(new Error('No valid employee data found in CSV'));
            return;
          }

          resolve(employeeData);
        } catch (error) {
          reject(new Error(`Failed to parse CSV: ${error.message}`));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const bulkImportEmployeeMapping = async (employeeData) => {
    const results = {
      total: employeeData.length,
      successful: 0,
      failed: 0,
      staffCreated: 0,
      staffUpdated: 0,
      mappingCreated: 0,
      errors: []
    };

    setCsvImportStatus('Importing employee mappings and staff records...');
    setCsvImportProgress(0);

    for (let i = 0; i < employeeData.length; i++) {
      const { login, employeeId, role, status, originalRow } = employeeData[i];
      
      try {
        // 1. Create/Update Staff Record
        const staffData = {
          login: login,
          role: role,
          status: status,
          source: 'csv_import',
          importedAt: serverTimestamp(),
          importedBy: loginEmail || 'admin'
        };

        // Check if staff already exists
        const staffRef = doc(db, 'staff', login);
        const staffSnap = await getDoc(staffRef);
        
        if (staffSnap.exists()) {
          // Update existing staff
          await updateDoc(staffRef, {
            ...staffData,
            updatedAt: serverTimestamp()
          });
          results.staffUpdated++;
          console.log(`Updated staff: ${login}`);
        } else {
          // Create new staff
          await setDoc(staffRef, {
            ...staffData,
            createdAt: serverTimestamp()
          });
          results.staffCreated++;
          console.log(`Created staff: ${login}`);
        }

        // 2. Create Employee Mapping
        await setDoc(doc(db, 'employeeMapping', login), {
          login: login,
          employeeId: employeeId,
          source: 'csv_import',
          importedAt: serverTimestamp(),
          importedBy: loginEmail || 'admin',
          originalRow: originalRow
        });
        results.mappingCreated++;

        results.successful++;
        console.log(`Imported: ${login} -> ${employeeId} (Role: ${role}, Status: ${status})`);
      } catch (error) {
        results.failed++;
        results.errors.push({
          login,
          employeeId,
          role,
          status,
          row: originalRow,
          error: error.message
        });
        console.error(`Failed to import ${login}:`, error);
      }

      // Update progress
      const progress = Math.round(((i + 1) / employeeData.length) * 100);
      setCsvImportProgress(progress);
    }

    return results;
  };

  const handleCsvFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setCsvImportStatus('Please select a CSV file');
        return;
      }
      
      setCsvFile(file);
      setCsvImportStatus(`Selected: ${file.name}`);
      setCsvImportResults(null);
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile) {
      setCsvImportStatus('Please select a CSV file first');
      return;
    }

    if (!canAddStaff) {
      setCsvImportStatus('Access denied. Admin authorization required.');
      return;
    }

    try {
      setCsvImportStatus('Parsing CSV file...');
      setCsvImportProgress(0);
      
      const employeeData = await parseCsvFile(csvFile);
      
      setCsvImportStatus(`Found ${employeeData.length} valid employee records. Starting import...`);
      
      const results = await bulkImportEmployeeMapping(employeeData);
      
      setCsvImportResults(results);
      setCsvImportStatus(`Import complete: ${results.successful} successful, ${results.failed} failed`);
      setCsvImportProgress(100);
      
      // Clear file input
      setCsvFile(null);
      document.getElementById('csvFileInput').value = '';
      
    } catch (error) {
      setCsvImportStatus(`Import failed: ${error.message}`);
      setCsvImportResults(null);
      console.error('CSV Import Error:', error);
    }
  };

  const [selectedTab, setSelectedTab] = useState('Plan');
  const [selectedUnassignedTab, setSelectedUnassignedTab] = useState('Unassigned');
  const [selectedBoardTab, setSelectedBoardTab] = useState('MOD');
  const [selectedFloor, setSelectedFloor] = useState('A01'); // Track selected floor
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [switchMode, setSwitchMode] = useState(false); // Track switch mode
  const [switchModeStaff, setSwitchModeStaff] = useState(null); // Track which staff is in switch mode
  const [allStaff, setAllStaff] = useState([]); // All staff from Firebase
  const [assignedStaff, setAssignedStaff] = useState({
    A01: {
      westSideWaterspider: [],
      westToteRunner: [],
      westPA: [],
      eastSpider: [],
      eastPA: [],
      eastRunner: [],
      eastVRC: []
    },
    A02: {
      westSideWaterspider: [],
      westToteRunner: [],
      westJamClear: [],
      westPA: [],
      eastSpider: [],
      eastPA: []
    },
    A03: {
      westSideWaterspider: [],
      westToteRunner: [],
      westPA: [],
      eastSpider: [],
      eastPA: []
    },
    A04: {
      westSideWaterspider: [],
      westToteRunner: [],
      westPA: [],
      eastSpider: [],
      eastPA: []
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingStep, setLoadingStep] = useState('Initializing...');

  // State for editing functionality
  const [editingId, setEditingId] = useState(null);
  const [newStaff, setNewStaff] = useState({ login: '', role: '', status: 'red' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState(''); // Track selected role filter
  const [showRoleFilter, setShowRoleFilter] = useState(false); // Track if role filter dropdown is open
  const [roleLocks, setRoleLocks] = useState({}); // Track which staff have locked roles
  // West PA multi-floor assignment modal state
  const [showWestPAModal, setShowWestPAModal] = useState(false);
  const [westPAOptions, setWestPAOptions] = useState([]); // list of floors e.g., ['A01','A02']
  const [westPASelection, setWestPASelection] = useState('');
  const [westPAStaff, setWestPAStaff] = useState([]); // snapshot of staff being assigned
  const [westPAMode, setWestPAMode] = useState('plan'); // 'plan' | 'approved'
  const [globalSortByAssignment, setGlobalSortByAssignment] = useState(false); // Track if Global tab is sorted by assignment

  // Plan management system
  const [planData, setPlanData] = useState({
    A01: {
      westSideWaterspider: [],
      westToteRunner: [],
      westPA: [],
      eastSpider: [],
      eastPA: [],
      eastRunner: [],
      eastVRC: []
    },
    A02: {
      westSideWaterspider: [],
      westToteRunner: [],
      westJamClear: [],
      westPA: [],
      eastSpider: [],
      eastPA: []
    },
    A03: {
      westSideWaterspider: [],
      westToteRunner: [],
      westPA: [],
      eastSpider: [],
      eastPA: []
    },
    A04: {
      westSideWaterspider: [],
      westToteRunner: [],
      westPA: [],
      eastSpider: [],
      eastPA: []
    }
  }); // Current plan being worked on
  const [savedPlans, setSavedPlans] = useState([]); // List of saved plans
  const [selectedPlan, setSelectedPlan] = useState(''); // Currently selected plan in Approved tab
  const [previouslySelectedPlan, setPreviouslySelectedPlan] = useState(''); // Track previously selected plan for persistence
  const [approvedPlanData, setApprovedPlanData] = useState(null); // Store the data of the currently loaded plan in Approved tab
  const [planMode, setPlanMode] = useState('plan'); // 'plan' or 'approved'

  // Plan creation state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [planDateInput, setPlanDateInput] = useState('');
  const [selectedShift, setSelectedShift] = useState('FHD'); // Default to FHD

  // Collaborative editing state
  const [activeUsers, setActiveUsers] = useState([]); // Users currently editing
  const [lastSyncTime, setLastSyncTime] = useState(null); // Last sync timestamp
  const [isOnline, setIsOnline] = useState(true); // Online status
  const [pendingChanges, setPendingChanges] = useState(false); // Unsaved changes indicator
  const [collaborativeUserId] = useState(() => `user_${Math.random().toString(36).substr(2, 9)}`); // Generate unique user ID
  const [planListeners, setPlanListeners] = useState({}); // Store active listeners
  const planCleanupIntervalRef = useRef(null); // Store cleanup interval ID using ref
  const savedPlansRef = useRef([]); // Store current plans using ref for cleanup access
  const [cleanupNotification, setCleanupNotification] = useState(null); // Store cleanup notification message

  // Dark mode state - Default to dark mode for better formatting
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('staffingBoardDarkMode');
    return saved ? JSON.parse(saved) : true; // Default to dark mode
  });

  // CSV Import state (Admin only)
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImportStatus, setCsvImportStatus] = useState('');
  const [csvImportProgress, setCsvImportProgress] = useState(0);
  const [csvImportResults, setCsvImportResults] = useState(null);

  // Login state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Check if current user can add staff (using secure credential validation)
  const canAddStaff = isLoggedIn && validateAdminCredentials(loginEmail, loginPassword);

  // Ticket creation state
  const [ticketForm, setTicketForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'SCC', // Default to SCC
    subCategory: 'staffing', // New sub-category field
    assignedTo: 'angel', // Default to angel
    floor: selectedFloor
  });
  // Ticket form visibility state - currently not used but kept for future ticket functionality
  // eslint-disable-next-line no-unused-vars
  const [showTicketForm, setShowTicketForm] = useState(false);

  // Clock-in status integration state
  const [clockInStatus, setClockInStatus] = useState({});
  const [isClockInPolling, setIsClockInPolling] = useState(false);
  const [clockInLastUpdate, setClockInLastUpdate] = useState(null);

  // Broadcast system state
  const [broadcastPlan, setBroadcastPlan] = useState(null); // Currently broadcasted plan
  const [broadcastStatus, setBroadcastStatus] = useState('idle'); // 'idle', 'broadcasting', 'error'

  // Monitor status (for header button color)
  const [monitorStatus, setMonitorStatus] = useState('checking'); // 'operational' | 'degraded' | 'error' | 'checking'

  useEffect(() => {
    let isMounted = true;
    const fetchMonitorStatus = async () => {
      try {
        const [healthRes, dbRes] = await Promise.all([
          fetch('https://staff.carrte.dev/api/health', { cache: 'no-cache' }),
          fetch('https://staff.carrte.dev/api/db-status', { cache: 'no-cache' })
        ]);
        if (!isMounted) return;
        let status = 'checking';
        if (dbRes.ok) {
          const db = await dbRes.json();
          // db.status is one of 'operational' | 'degraded' | 'error'
          status = db?.status || 'checking';
        } else if (healthRes.ok) {
          const health = await healthRes.json();
          // Map 'healthy'/'unhealthy' to our tri-state
          if (health?.status === 'healthy') status = 'operational';
          else if (health?.status === 'unhealthy') status = 'error';
          else status = 'checking';
        } else {
          status = 'error';
        }
        setMonitorStatus(status);
      } catch (_) {
        if (isMounted) setMonitorStatus('error');
      }
    };
    fetchMonitorStatus();
    const id = setInterval(fetchMonitorStatus, 60000);
    return () => { isMounted = false; clearInterval(id); };
  }, []);


  // Compute unassigned staff by filtering out assigned staff from ALL floors
  const unassignedStaff = allStaff.filter(staff => {
    const allAssignedLogins = assignedStaff ? Object.values(assignedStaff).flatMap(floor => 
      floor ? Object.values(floor).flat().map(assigned => assigned.login) : []
    ) : [];
    return !allAssignedLogins.includes(staff.login);
  });

  // Get unique roles from all staff for filtering
  const getUniqueRoles = () => {
    const roles = new Set();
    allStaff.forEach(staff => {
      if (staff.role && staff.role.trim()) {
        // Split roles by comma and trim whitespace
        const individualRoles = staff.role.split(',').map(role => role.trim()).filter(role => role.length > 0);
        individualRoles.forEach(role => {
          roles.add(role);
        });
      }
    });
    return Array.from(roles).sort();
  };

  // Filter staff based on search term and role filter
  const getFilteredStaff = (staffList) => {
    return staffList.filter(staff => {
      const matchesSearch = staff.login.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Check if staff has the selected role (handling comma-separated roles)
      let matchesRole = true;
      if (selectedRoleFilter) {
        if (staff.role && staff.role.trim()) {
          const staffRoles = staff.role.split(',').map(role => role.trim());
          matchesRole = staffRoles.includes(selectedRoleFilter);
        } else {
          matchesRole = false;
        }
      }
      
      return matchesSearch && matchesRole;
    });
  };

  // Compute filtered unassigned staff
  const filteredUnassignedStaff = getFilteredStaff(unassignedStaff);
  
  // Function to check if a staff member is assigned to any role
  const isStaffAssigned = (staffLogin) => {
    for (const floor of Object.keys(assignedStaff)) {
      for (const area of Object.keys(assignedStaff[floor])) {
        if (assignedStaff[floor][area].find(assigned => assigned.login === staffLogin)) {
          return true;
        }
      }
    }
    return false;
  };

  // Compute filtered all staff for Global tab
  const filteredAllStaff = getFilteredStaff(allStaff);

  // Handle Assignment header click for sorting
  const handleAssignmentSort = () => {
    setGlobalSortByAssignment(!globalSortByAssignment);
  };

  // Get sorted staff list for Global tab
  const getSortedGlobalStaff = () => {
    if (!globalSortByAssignment) {
      return filteredAllStaff;
    }
    
    // Sort by: assigned first, then by primary floor order: A01, A02, A03, A04
    const floorPriority = ['A01', 'A02', 'A03', 'A04'];

    const isOnFloor = (login, floor) => {
      const floorData = assignedStaff[floor];
      if (!floorData) return false;
      for (const area of Object.keys(floorData)) {
        const list = floorData[area] || [];
        if (list.find(assigned => assigned.login === login)) {
          return true;
        }
      }
      return false;
    };

    const getPrimaryFloorIndex = (login) => {
      for (let i = 0; i < floorPriority.length; i += 1) {
        if (isOnFloor(login, floorPriority[i])) return i;
      }
      return floorPriority.length; // unassigned or not matched
    };

    return [...filteredAllStaff].sort((a, b) => {
      const aAssigned = isStaffAssigned(a.login);
      const bAssigned = isStaffAssigned(b.login);

      if (aAssigned && !bAssigned) return -1; // assigned first
      if (!aAssigned && bAssigned) return 1;  // assigned first
      if (!aAssigned && !bAssigned) return 0; // both unassigned

      // Both assigned – sort by primary floor order
      const aIdx = getPrimaryFloorIndex(a.login);
      const bIdx = getPrimaryFloorIndex(b.login);
      if (aIdx !== bIdx) return aIdx - bIdx;

      // Stable/deterministic tie-breaker (same primary floor)
      return a.login.localeCompare(b.login);
    });
  };

  // Get current floor's assigned staff for display
  const currentFloorAssignedStaff = (() => {
    const floorData = assignedStaff && assignedStaff[selectedFloor];
    
    if (floorData) {
      // Ensure A02 has westJamClear property
      if (selectedFloor === 'A02' && !floorData.westJamClear) {
        return {
          ...floorData,
          westJamClear: []
        };
      }
      // Ensure A01 has eastRunner and eastVRC properties
      if (selectedFloor === 'A01' && (!floorData.eastRunner || !floorData.eastVRC)) {
        return {
          ...floorData,
          eastRunner: floorData.eastRunner || [],
          eastVRC: floorData.eastVRC || []
        };
      }
      return floorData;
    }
    
    // Fallback object
    const baseStaff = {
      westSideWaterspider: [],
      westToteRunner: [],
      westPA: [],
      eastSpider: [],
      eastPA: []
    };
    
    if (selectedFloor === 'A01') {
      return {
        ...baseStaff,
        eastRunner: [],
        eastVRC: []
      };
    } else if (selectedFloor === 'A02') {
      return {
        ...baseStaff,
        westJamClear: []
      };
    }
    
    return baseStaff;
  })();

  // Handle clicking outside filter dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRoleFilter && !event.target.closest('.role-filter-container')) {
        setShowRoleFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRoleFilter]);

  // Test role locks write permissions
  const testRoleLocksWrite = async () => {
    try {
      await setDoc(doc(db, 'role_locks', 'test'), {
        test: true,
        timestamp: new Date()
      }, { merge: true });
      console.log('Role locks write test successful');
      return true;
    } catch (error) {
      console.error('Role locks write test failed:', error);
      return false;
    }
  };

  // Test saved plans write permissions
  const testSavedPlansWrite = async () => {
    try {
      await setDoc(doc(db, 'saved_plans', 'test'), {
        test: true,
        timestamp: new Date()
      }, { merge: true });
      console.log('Saved plans write test successful');
      return true;
    } catch (error) {
      console.error('Saved plans write test failed:', error);
      return false;
    }
  };

  // Test Supabase connection
  const testSupabaseConnection = async () => {
    try {
      const result = await ticketService.testConnection();
      if (result.success) {
        console.log('Supabase connection test successful');
        return true;
      } else {
        console.error('Supabase connection test failed:', result.error);
        return false;
      }
    } catch (error) {
      console.error('Supabase connection test error:', error);
      return false;
    }
  };

  // Function to clean up expired plans (older than 24 hours)
  const cleanupExpiredPlans = useCallback(async (plans) => {
    try {
      // Use passed plans or fall back to ref if no plans provided
      const plansToCheck = plans || savedPlansRef.current || [];
      
      // If no plans to check, exit early
      if (!plansToCheck || plansToCheck.length === 0) {
        console.log('No plans to check for cleanup');
        return;
      }
      
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));
      
      const expiredPlans = plansToCheck.filter(plan => {
        if (!plan || !plan.createdAt) return false;
        
        // Handle different types of createdAt values
        let createdAtDate;
        if (plan.createdAt instanceof Date) {
          createdAtDate = plan.createdAt;
        } else if (plan.createdAt && typeof plan.createdAt === 'object' && plan.createdAt.seconds) {
          // Firebase Timestamp object
          createdAtDate = new Date(plan.createdAt.seconds * 1000);
        } else if (plan.createdAt && typeof plan.createdAt === 'string') {
          // String date
          createdAtDate = new Date(plan.createdAt);
        } else {
          return false;
        }
        
        // Validate the date
        if (isNaN(createdAtDate.getTime())) {
          return false;
        }
        
        return createdAtDate < twentyFourHoursAgo;
      });

      if (expiredPlans.length > 0) {
        console.log(`Found ${expiredPlans.length} expired plans to clean up`);
        
        // Delete expired plans from Firebase
        for (const plan of expiredPlans) {
          try {
            await deleteDoc(doc(db, 'saved_plans', plan.id));
            console.log(`Deleted expired plan: ${plan.name}`);
          } catch (error) {
            console.error(`Failed to delete expired plan ${plan.name}:`, error);
          }
        }
        
        // Update local state by removing expired plans
        setSavedPlans(prevPlans => {
          const updated = prevPlans.filter(plan => 
            !expiredPlans.some(expired => expired.id === plan.id)
          );
          savedPlansRef.current = updated; // Update ref for cleanup access
          return updated;
        });
        
        // If any of the expired plans were currently selected, clear the selection
        setSelectedPlan(prevSelectedPlan => {
          if (expiredPlans.some(plan => plan.id === prevSelectedPlan)) {
            setPreviouslySelectedPlan('');
            setApprovedPlanData(null);
            setActiveUsers([]);
            return '';
          }
          return prevSelectedPlan;
        });
        
        console.log(`Successfully cleaned up ${expiredPlans.length} expired plans`);
        
        // Show notification to user
        setCleanupNotification({
          message: `Automatically cleaned up ${expiredPlans.length} expired plan${expiredPlans.length > 1 ? 's' : ''}`,
          timestamp: new Date(),
          type: 'success'
        });
        
        // Auto-hide notification after 5 seconds
        setTimeout(() => setCleanupNotification(null), 5000);
      }
    } catch (error) {
      console.error('Error during plan cleanup:', error);
      
      // Show error notification
      setCleanupNotification({
        message: `Error during cleanup: ${error.message}`,
        timestamp: new Date(),
        type: 'error'
      });
      
      // Auto-hide error notification after 8 seconds
      setTimeout(() => setCleanupNotification(null), 8000);
    }
  }, []);

  // Initialize Firebase and subscribe to real-time updates
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Only load data after successful authentication
    if (!isAuthenticated) {
      return;
    }

    let unsubscribe = null;
    let unsubscribeAssigned = null;
    let timeoutId = null;
    let retryCount = 0;
    const maxRetries = 3;

    const initializeApp = async () => {
      try {
        setLoading(true);
        setError(null);
        setLoadingStep(`Connecting to Firebase... (Attempt ${retryCount + 1}/${maxRetries})`);
        
        // Test Firebase connection first
        const isConnected = await testFirebaseConnection();
        if (!isConnected) {
          throw new Error('Firebase connection test failed');
        }
        
        // Test role locks write permissions
        const canWriteRoleLocks = await testRoleLocksWrite();
        if (!canWriteRoleLocks) {
          console.warn('Role locks write permissions not available - role locking will not work');
        }
        
        // Test saved plans write permissions
        const canWriteSavedPlans = await testSavedPlansWrite();
        if (!canWriteSavedPlans) {
          console.warn('Saved plans write permissions not available - plan saving will not work');
        }
        
        // Set a timeout to prevent infinite loading
        timeoutId = setTimeout(() => {
          console.error('Firebase connection timeout');
          if (retryCount < maxRetries - 1) {
            retryCount++;
            console.log(`Retrying connection (${retryCount}/${maxRetries})`);
            initializeApp();
          } else {
            setError('Connection timeout. Please check your internet connection and Firebase configuration.');
            setLoading(false);
          }
        }, 10000); // 10 second timeout
        
        // Start real-time listeners immediately
        setLoadingStep('Setting up real-time connections...');
        
        // Subscribe to all staff
        unsubscribe = subscribeToStaff((staff) => {
          console.log('All staff data received, clearing timeout');
          setLoadingStep('Data loaded successfully!');
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          setAllStaff(staff);
          setLoading(false);
        });

        // Subscribe to assigned staff
        unsubscribeAssigned = subscribeToAssignedStaff((assignedStaffData) => {
          console.log('Assigned staff data received');
          setAssignedStaff(assignedStaffData);
        });

        // Load role locks
        setLoadingStep('Loading role locks...');
        try {
          const roleLocksDoc = await getDoc(doc(db, 'role_locks', 'locks'));
          if (roleLocksDoc.exists()) {
            setRoleLocks(roleLocksDoc.data().locks || {});
          }
        } catch (error) {
          console.log('No role locks found, starting fresh');
          setRoleLocks({});
        }

        // Load saved plans
        setLoadingStep('Loading saved plans...');
        try {
          const plansSnapshot = await getDocs(collection(db, 'saved_plans'));
          const plans = [];
          plansSnapshot.forEach(doc => {
            const planData = doc.data();
            plans.push({ 
              id: doc.id, 
              ...planData,
              createdAt: convertTimestamp(planData.createdAt)
            });
          });
          setSavedPlans(plans);
          savedPlansRef.current = plans; // Update ref for cleanup access
          console.log('Loaded saved plans:', plans.length);
          
          // Clean up expired plans (older than 24 hours)
          // eslint-disable-next-line react-hooks/exhaustive-deps
          await cleanupExpiredPlans(plans);
        } catch (error) {
          console.log('No saved plans found, starting fresh');
          setSavedPlans([]);
        }



        // Test Supabase connection
        setLoadingStep('Testing Supabase connection...');
        try {
          const supabaseTest = await testSupabaseConnection();
          if (supabaseTest) {
            console.log('Supabase connection test successful');
          } else {
            console.warn('Supabase connection test failed, but continuing...');
          }
        } catch (error) {
          console.warn('Supabase connection test error, but continuing...');
        }

        // Initialize clock-in status integration
        setLoadingStep('Initializing clock-in status integration...');
        try {
          // Start polling for clock-in status updates
          clockInService.startPolling((statusMap) => {
            setClockInStatus(statusMap);
            setClockInLastUpdate(new Date());
            updateStaffStatusFromClockIn(statusMap);
            console.log('Clock-in status updated:', statusMap);
          }, 30000); // Poll every 30 seconds
          setIsClockInPolling(true);
          console.log('Clock-in status polling started');
        } catch (error) {
          console.warn('Clock-in status integration failed, but continuing...', error);
        }

        // Set up periodic plan cleanup (every hour)
        setLoadingStep('Setting up automatic plan cleanup...');
        const cleanupInterval = setInterval(async () => {
          console.log('Running scheduled plan cleanup...');
          // eslint-disable-next-line react-hooks/exhaustive-deps
          await cleanupExpiredPlans(savedPlans);
        }, 60 * 60 * 1000); // Run every hour (60 minutes * 60 seconds * 1000 milliseconds)
        
        // Store the interval ID for cleanup using ref
        planCleanupIntervalRef.current = cleanupInterval;
        console.log('Automatic plan cleanup scheduled (every hour)');

      } catch (err) {
        console.error('Error initializing app:', err);
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (retryCount < maxRetries - 1) {
          retryCount++;
          console.log(`Retrying after error (${retryCount}/${maxRetries})`);
          setTimeout(initializeApp, 2000); // Retry after 2 seconds
        } else {
          setError('Failed to connect to database. Please check your Firebase configuration and ensure Firestore is enabled.');
          setLoading(false);
        }
      }
    };

    initializeApp();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (unsubscribeAssigned) {
        unsubscribeAssigned();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Stop clock-in status polling
      clockInService.stopPolling();
      setIsClockInPolling(false);
      // Clear plan cleanup interval
      if (planCleanupIntervalRef.current) {
        clearInterval(planCleanupIntervalRef.current);
      }
    };
  }, [isAuthenticated]);

  const handleStaffSelect = (staff) => {
    if (switchMode) {
      // If already in switch mode, clear it and start fresh
      setSwitchMode(false);
      setSwitchModeStaff(null);
      setSelectedStaff([]);
    } else {
      // Check if this staff is already assigned
      const isAssigned = Object.keys(assignedStaff[selectedFloor]).some(area => 
        assignedStaff[selectedFloor][area].find(assigned => assigned.login === staff.login)
      );
      
      if (isAssigned) {
        // Check if role is locked
        if (roleLocks[staff.login]) {
          alert(`Cannot switch ${staff.login}'s role - Role is locked. Click the lock icon to unlock.`);
          return;
        }
        
        // Enter switch mode for this specific staff
        setSwitchMode(true);
        setSwitchModeStaff(staff);
        setSelectedStaff([staff]);
      } else {
        // Normal selection for unassigned staff
        setSelectedStaff(prev => {
          const isSelected = prev.find(s => s.login === staff.login);
          if (isSelected) {
            return prev.filter(s => s.login !== staff.login);
          } else {
            return [...prev, staff];
          }
        });
      }
    }
  };

  // Handle staff assignment to different areas
  const handleAssignStaff = async (area) => {
    if (selectedStaff.length === 0) {
      alert('Please select staff members to assign');
      return;
    }

    if (planMode === 'plan') {
      // Use plan-specific assignment for Plan tab
      handleAssignStaffInPlan(area);
    } else if (planMode === 'approved' && selectedPlan) {
      // Allow editing in Approved tab when a plan is selected
      handleAssignStaffInPlan(area);
    } else {
      // Use original assignment for Approved tab (should not be called)
      alert('Cannot modify assignments in Approved mode. Please use Plan tab to make changes or select a plan to edit.');
    }
  };

  // Handle staff restaffing (switch mode)
  const handleRestaff = async (area) => {
    if (!switchModeStaff) {
      alert('No staff selected for restaffing');
      return;
    }

    if (planMode === 'plan') {
      // Use plan-specific restaffing for Plan tab
      handleRestaffInPlan(area);
    } else if (planMode === 'approved' && selectedPlan) {
      // Allow restaffing in Approved tab when a plan is selected
      handleRestaffInPlan(area);
    } else {
      // Use original restaffing for Approved tab (should not be called)
      alert('Cannot restaff in Approved mode. Please use Plan tab to make changes or select a plan to edit.');
    }
  };

  // Handle staff restaffing in plan mode (local state only)
  const handleRestaffInPlan = async (area) => {
    if (!switchModeStaff) {
      alert('No staff selected for restaffing');
      return;
    }

    try {
      // First, remove staff from their current assignments
      const updatedAssignedStaff = { ...assignedStaff };
      
      // Remove selected staff from all areas on current floor
      Object.keys(updatedAssignedStaff[selectedFloor]).forEach(currentArea => {
        updatedAssignedStaff[selectedFloor][currentArea] = updatedAssignedStaff[selectedFloor][currentArea].filter(
          staff => staff.login !== switchModeStaff.login
        );
      });

      // Also remove from linked East PA if applicable
      if (selectedFloor === 'A03' || selectedFloor === 'A04') {
        const linkedFloor = selectedFloor === 'A03' ? 'A04' : 'A03';
        updatedAssignedStaff[linkedFloor].eastPA = updatedAssignedStaff[linkedFloor].eastPA.filter(
          staff => staff.login !== switchModeStaff.login
        );
      }

      // Also remove from linked East PA for A01/A02 if applicable
      if (selectedFloor === 'A01' || selectedFloor === 'A02') {
        const linkedFloor = selectedFloor === 'A01' ? 'A02' : 'A01';
        updatedAssignedStaff[linkedFloor].eastPA = updatedAssignedStaff[linkedFloor].eastPA.filter(
          staff => staff.login !== switchModeStaff.login
        );
      }

      // Then add them to the new area (block direct A03 westToteRunner restaff)
      if (area === 'westToteRunner' && selectedFloor === 'A03') {
        alert('West Tote Runner on A03 is auto-filled from A02/A04. Please restaff on A02 or A04.');
        return;
      }

      updatedAssignedStaff[selectedFloor][area] = [
        ...updatedAssignedStaff[selectedFloor][area],
        switchModeStaff
      ];

      // Handle linked East PA assignments
      if (area === 'eastPA' && (selectedFloor === 'A03' || selectedFloor === 'A04')) {
        const linkedFloor = selectedFloor === 'A03' ? 'A04' : 'A03';
        updatedAssignedStaff[linkedFloor].eastPA = [
          ...updatedAssignedStaff[linkedFloor].eastPA,
          switchModeStaff
        ];
      }

      // Handle linked West Tote Runner assignments to A03 from A02/A04
      if (area === 'westToteRunner' && (selectedFloor === 'A02' || selectedFloor === 'A04')) {
        updatedAssignedStaff['A03'].westToteRunner = [
          ...updatedAssignedStaff['A03'].westToteRunner,
          switchModeStaff
        ];
      }

      // Handle linked East PA assignments for A01/A02
      if (area === 'eastPA' && (selectedFloor === 'A01' || selectedFloor === 'A02')) {
        const linkedFloor = selectedFloor === 'A01' ? 'A02' : 'A01';
        updatedAssignedStaff[linkedFloor].eastPA = [
          ...updatedAssignedStaff[linkedFloor].eastPA,
          switchModeStaff
        ];
      }

      setAssignedStaff(updatedAssignedStaff);

      // Update planData if in Plan mode
      if (planMode === 'plan') {
        setPlanData(updatedAssignedStaff);
      }

      // Clear switch mode
      setSwitchMode(false);
      setSwitchModeStaff(null);
      setSelectedStaff([]);
      
      console.log('Staff restaffed successfully to', area);
    } catch (error) {
      console.error('Error restaffing staff:', error);
      alert('Failed to restaff staff. Please try again.');
    }
  };

  // Handle unassigning staff from areas
  const handleUnassignStaff = async (staffToRemove, area) => {
    if (planMode === 'plan') {
      // Use plan-specific unassignment for Plan tab
      handleUnassignStaffInPlan(staffToRemove, area);
    } else if (planMode === 'approved' && selectedPlan) {
      // Allow editing in Approved tab when a plan is selected
      handleUnassignStaffInPlan(staffToRemove, area);
    } else {
      // Use original unassignment for Approved tab (should not be called)
      alert('Cannot modify assignments in Approved mode. Please use Plan tab to make changes or select a plan to edit.');
    }
  };

  // Add new staff member
  const handleAddStaff = async () => {
    if (newStaff.login.trim()) {
      try {
        console.log('Attempting to add staff:', newStaff);
        await addStaff(newStaff);
        console.log('Staff added successfully');
        setNewStaff({ login: '', role: '', status: 'red' });
        setShowAddForm(false);
      } catch (error) {
        console.error('Error adding staff:', error);
        if (error.message.includes('already exists')) {
          alert(`Error: ${error.message}`);
        } else if (error.code === 'permission-denied') {
          alert('Permission denied. Please check your Firebase security rules.');
        } else if (error.code === 'unavailable') {
          alert('Firebase is unavailable. Please check your internet connection.');
        } else {
          alert(`Failed to add staff member: ${error.message}`);
        }
      }
    }
  };

  // Delete staff member
  const handleDeleteStaff = async (id) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await deleteStaff(id);
        setSelectedStaff(selectedStaff.filter(staff => staff.id !== id));
      } catch (error) {
        console.error('Error deleting staff:', error);
        alert('Failed to delete staff member. Please try again.');
      }
    }
  };

  // Start editing staff member
  const handleStartEdit = (staff) => {
    setEditingId(staff.id);
  };

  // Save edited staff member
  const handleSaveEdit = async (id, updates) => {
    try {
      await updateStaff(id, updates);
      setEditingId(null);
    } catch (error) {
      console.error('Error updating staff:', error);
      if (error.message.includes('already exists')) {
        alert(`Error: ${error.message}`);
      } else {
        alert('Failed to update staff member. Please try again.');
      }
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
  };

  // Reset to default staff (useful for testing)
  const handleResetToDefault = async () => {
    if (window.confirm('Are you sure you want to reset to default staff? This will clear all current staff and assignments.')) {
      try {
        // Clear all assigned staff from Firebase
        await clearAllAssignedStaff();
        console.log('Assigned staff cleared from Firebase');
        
        // Clear local state for assigned staff
        const emptyAssignedStaff = {
          A01: { westSideWaterspider: [], westToteRunner: [], westPA: [], eastSpider: [], eastPA: [], eastRunner: [], eastVRC: [] },
          A02: { westSideWaterspider: [], westToteRunner: [], westPA: [], eastSpider: [], eastPA: [] },
          A03: { westSideWaterspider: [], westToteRunner: [], westPA: [], eastSpider: [], eastPA: [] },
          A04: { westSideWaterspider: [], westToteRunner: [], westPA: [], eastSpider: [], eastPA: [] }
        };
        
        setAssignedStaff(emptyAssignedStaff);
        setPlanData(emptyAssignedStaff);
        
        console.log('All staff reset to default - local state cleared');
      } catch (error) {
        console.error('Error resetting staff:', error);
        alert('Failed to reset staff. Please try again.');
      }
    }
  };

  // Handle toggling role lock for a staff member
  const handleToggleRoleLock = async (staffId, staffLogin) => {
    try {
      const newRoleLocks = { ...roleLocks };
      if (newRoleLocks[staffLogin]) {
        delete newRoleLocks[staffLogin];
      } else {
        newRoleLocks[staffLogin] = true;
      }
      setRoleLocks(newRoleLocks);
      
      // Persist role locks to Firebase - use setDoc to create if doesn't exist
      await setDoc(doc(db, 'role_locks', 'locks'), {
        locks: newRoleLocks,
        updatedAt: new Date()
      }, { merge: true });
      
      console.log(`Role lock ${newRoleLocks[staffLogin] ? 'enabled' : 'disabled'} for ${staffLogin}`);
    } catch (error) {
      console.error('Error updating role lock:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      alert('Failed to update role lock. Please check console for details.');
    }
  };

  // Load role locks from Firebase

  // Plan management functions
  const handleTabChange = (tab) => {
    console.log('Tab change to:', tab, 'Current assignedStaff:', assignedStaff);
    setSelectedTab(tab);
    if (tab === 'Plan') {
      setPlanMode('plan');
      // Use planData for Plan tab
      setAssignedStaff(planData);
      console.log('Switched to Plan tab, using planData:', planData);
      // Store the current selected plan before switching to Plan tab
      if (selectedPlan) {
        setPreviouslySelectedPlan(selectedPlan);
      }
    } else if (tab === 'Approved') {
      setPlanMode('approved');
      // If there's a previously selected plan, restore it
      if (previouslySelectedPlan && !selectedPlan) {
        setSelectedPlan(previouslySelectedPlan);
        console.log('Restoring previously selected plan:', previouslySelectedPlan);
      }
      // Restore the approved plan data if it exists
      if (approvedPlanData) {
        setAssignedStaff(approvedPlanData);
        console.log('Restored approved plan data:', approvedPlanData);
      }
      console.log('Switched to Approved tab, assignedStaff:', assignedStaff);
    }
  };

  // Helper function to convert Firestore timestamp to Date
  const convertTimestamp = (timestamp) => {
    if (!timestamp) return new Date();
    if (timestamp.toDate) {
      return timestamp.toDate();
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000);
    }
    return new Date(timestamp);
  };

  // Helper function to format date consistently
  const formatDateForDisplay = (dateString) => {
    // Safety check for undefined/null values
    if (!dateString || typeof dateString !== 'string') {
      console.warn('formatDateForDisplay called with invalid dateString:', dateString);
      return '';
    }
    
    // Handle both MM/DD/YYYY and M/D/YYYY formats
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${month}/${day}/${year}`;
    }
    return dateString;
  };

  // Helper function to validate and normalize date input
  const normalizeDate = (dateString) => {
    // Remove extra whitespace
    const cleaned = dateString.trim();
    
    // Check if it matches M/D/YYYY or MM/DD/YYYY format
    const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = cleaned.match(dateRegex);
    
    if (match) {
      const [, month, day, year] = match;
      const normalizedMonth = month.padStart(2, '0');
      const normalizedDay = day.padStart(2, '0');
      return `${normalizedMonth}/${normalizedDay}/${year}`;
    }
    
    return cleaned; // Return as-is if doesn't match expected format
  };

  // Helper function to get today's date in MM/DD/YYYY format
  const getTodayFormatted = () => {
    const today = new Date();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    const year = today.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Helper function to check if a plan is close to expiring (within 2 hours)
  const isPlanCloseToExpiring = useCallback((plan) => {
    if (!plan || !plan.createdAt) return false;
    
    // Handle different types of createdAt values
    let createdAtDate;
    if (plan.createdAt instanceof Date) {
      createdAtDate = plan.createdAt;
    } else if (plan.createdAt && typeof plan.createdAt === 'object' && plan.createdAt.seconds) {
      // Firebase Timestamp object
      createdAtDate = new Date(plan.createdAt.seconds * 1000);
    } else if (plan.createdAt && typeof plan.createdAt === 'string') {
      // String date
      createdAtDate = new Date(plan.createdAt);
    } else {
      return false;
    }
    
    // Validate the date
    if (isNaN(createdAtDate.getTime())) {
      return false;
    }
    
    const now = new Date();
    const twentyTwoHoursAgo = new Date(now.getTime() - (22 * 60 * 60 * 1000));
    return createdAtDate < twentyTwoHoursAgo;
  }, []);



  // Helper function to get time until plan expires
  const getTimeUntilExpiry = useCallback((plan) => {
    if (!plan || !plan.createdAt) return 'Unknown';
    
    // Handle different types of createdAt values
    let createdAtDate;
    if (plan.createdAt instanceof Date) {
      createdAtDate = plan.createdAt;
    } else if (plan.createdAt && typeof plan.createdAt === 'object' && plan.createdAt.seconds) {
      // Firebase Timestamp object
      createdAtDate = new Date(plan.createdAt.seconds * 1000);
    } else if (plan.createdAt && typeof plan.createdAt === 'string') {
      // String date
      createdAtDate = new Date(plan.createdAt);
    } else {
      return 'Unknown';
    }
    
    // Validate the date
    if (isNaN(createdAtDate.getTime())) {
      return 'Invalid date';
    }
    
    const now = new Date();
    const expiryTime = new Date(createdAtDate.getTime() + (24 * 60 * 60 * 1000));
    const timeLeft = expiryTime - now;
    
    if (timeLeft <= 0) return 'Expired';
    
    const hours = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }, []);

  // Helper function to get area display name with conditional naming
  const getAreaDisplayName = (area, floor) => {
    // Special case: West Tote Runner becomes "West Runner" on floor A01
    if (area === 'westToteRunner' && floor === 'A01') {
      return 'West Runner';
    }
    
    // Special case: Conditional naming for A01 floor
    if (floor === 'A01') {
      if (area === 'eastPA') return 'East PA';
      if (area === 'eastRunner') return 'East Runner';
      if (area === 'eastVRC') return 'East VRC';
    }
    
    const areaNames = {
      westSideWaterspider: 'West Spider',
      westToteRunner: 'West Tote Runner',
      westJamClear: 'West Jam Clear',
      westPA: 'West PA',
      eastSpider: 'East Spider',
      eastPA: 'East PA',
      eastRunner: 'East Runner',
      eastVRC: 'East VRC'
    };
    return areaNames[area] || area;
  };

  // Broadcast system functions
  const pushToBroadcast = async (planId) => {
    try {
      setBroadcastStatus('broadcasting');
      
      // Find the plan to broadcast
      const planToBroadcast = savedPlans.find(plan => plan.id === planId);
      if (!planToBroadcast) {
        throw new Error('Plan not found');
      }

      // Load the full plan data if needed
      let planData;
      if (planToBroadcast.data && Object.keys(planToBroadcast.data).length > 0) {
        planData = planToBroadcast.data;
      } else {
        // Load from Firebase if data is not in local state
        const planDoc = await getDoc(doc(db, 'saved_plans', planId));
        if (planDoc.exists()) {
          planData = planDoc.data().data;
        } else {
          throw new Error('Plan data not found in Firebase');
        }
      }

      // Create broadcast plan object
      const broadcastPlanData = {
        id: planId,
        name: planToBroadcast.name,
        planDate: planToBroadcast.planDate,
        shift: planToBroadcast.shift,
        data: planData,
        createdAt: planToBroadcast.createdAt,
        broadcastAt: new Date(),
        status: 'broadcasting'
      };

      // Save to Firebase using existing saved_plans collection with broadcast flag
      await setDoc(doc(db, 'saved_plans', planId), {
        ...planToBroadcast,
        isBroadcasting: true,
        broadcastAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Update local state
      setBroadcastPlan(broadcastPlanData);
      setBroadcastStatus('broadcasting');
      
      console.log('Plan pushed to broadcast successfully:', planId);
    } catch (error) {
      console.error('Error pushing plan to broadcast:', error);
      setBroadcastStatus('error');
      alert('Failed to push plan to broadcast. Please try again.');
    }
  };

  const clearBroadcast = async () => {
    try {
      if (broadcastPlan) {
        // Clear broadcast flag from the plan in Firebase
        await setDoc(doc(db, 'saved_plans', broadcastPlan.id), {
          isBroadcasting: false,
          broadcastAt: null,
          updatedAt: serverTimestamp()
        }, { merge: true });
      }

      // Update local state
      setBroadcastPlan(null);
      setBroadcastStatus('idle');
      
      console.log('Broadcast cleared successfully');
    } catch (error) {
      console.error('Error clearing broadcast:', error);
      alert('Failed to clear broadcast. Please try again.');
    }
  };

  const handleSavePlan = async () => {
    // Show the date picker modal with properly formatted today's date
    setPlanDateInput(getTodayFormatted());
    setShowDatePicker(true);
  };

  const handleConfirmSavePlan = async () => {
    const rawPlanDate = planDateInput.trim();
    if (!rawPlanDate) {
      alert('Please enter a date for this plan');
      return;
    }

    if (!selectedShift) {
      alert('Please select a shift for this plan');
      return;
    }

    // Normalize the date (allows both M/D/YYYY and MM/DD/YYYY)
    const planDate = normalizeDate(rawPlanDate);

    // Validate the normalized date format
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    if (!dateRegex.test(planDate)) {
      alert('Please enter a valid date in M/D/YYYY or MM/DD/YYYY format (e.g., 8/9/2025 or 08/09/2025)');
      return;
    }

    // Convert the date string to a Date object for validation
    const selectedDate = new Date(planDate);
    if (isNaN(selectedDate.getTime())) {
      alert('Please enter a valid date');
      return;
    }

    // Check if the selected date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate date comparison
    if (selectedDate < today) {
      alert('Cannot create a plan for a date in the past. Please select today\'s date or a future date.');
      return;
    }

    // Check for duplicate plans (same normalized date and shift)
    const duplicatePlan = savedPlans.find(plan => {
      const formattedPlanDate = formatDateForDisplay(plan.planDate);
      // Skip plans with invalid dates
      if (!formattedPlanDate) {
        return false;
      }
      return formattedPlanDate === planDate && plan.shift === selectedShift;
    });
    
    if (duplicatePlan) {
      alert(`A plan for ${planDate} already exists for ${selectedShift} shift. Please choose a different date or shift.`);
      return;
    }

    // Format the plan name as "Plan for MM/DD/YYYY - SHIFT"
    const planName = `Plan for ${planDate} - ${selectedShift}`;

    try {
      console.log('Attempting to save plan:', planName);
      console.log('Current planData:', planData);
      
      const newPlan = {
        id: Date.now().toString(),
        name: planName,
        planDate: planDate, // Store the date separately for future reference
        shift: selectedShift, // Store the shift separately
        data: { ...planData },
        createdAt: new Date(), // Use current date for local state
        createdBy: collaborativeUserId,
        updatedAt: new Date(), // Use current date for local state
        updatedBy: collaborativeUserId,
        lastModified: new Date().toISOString()
      };

      console.log('Plan object to save:', newPlan);

      // Create Firebase object with server timestamps
      const firebasePlan = {
        ...newPlan,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Save to Firebase
      await setDoc(doc(db, 'saved_plans', newPlan.id), firebasePlan);
      console.log('Plan saved to Firebase successfully');
      
      // Update local state
      setSavedPlans(prev => {
        const updated = [...prev, newPlan];
        savedPlansRef.current = updated; // Update ref for cleanup access
        return updated;
      });
      
      // Close the modal and show success message
      setShowDatePicker(false);
      setPlanDateInput('');
      setSelectedShift('FHD'); // Reset to default
      const timeLeft = getTimeUntilExpiry(newPlan);
      alert(`Plan for ${planDate} - ${selectedShift} saved successfully! (Expires: ${timeLeft})`);
    } catch (error) {
      console.error('Error saving plan:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      alert(`Failed to save plan: ${error.message}. Please check console for details.`);
    }
  };

  const handleCancelSavePlan = () => {
    setShowDatePicker(false);
    setPlanDateInput('');
    setSelectedShift('FHD'); // Reset to default
  };

  const handleLoadPlan = async (planId) => {
    // Unsubscribe from previous plan if any
    if (selectedPlan && selectedPlan !== planId) {
      unsubscribeFromPlan(selectedPlan);
      updateUserPresence(selectedPlan, false); // Mark as inactive for previous plan
    }
    
    if (!planId) {
      // Clear the display when no plan is selected
      setAssignedStaff({
        A01: { westSideWaterspider: [], westToteRunner: [], westPA: [], eastSpider: [], eastPA: [], eastRunner: [], eastVRC: [] },
        A02: { westSideWaterspider: [], westToteRunner: [], westPA: [], eastSpider: [], eastPA: [] },
        A03: { westSideWaterspider: [], westToteRunner: [], westPA: [], eastSpider: [], eastPA: [] },
        A04: { westSideWaterspider: [], westToteRunner: [], westPA: [], eastSpider: [], eastPA: [] }
      });
      setSelectedPlan('');
      setPreviouslySelectedPlan(''); // Clear previously selected plan when user explicitly clears selection
      setApprovedPlanData(null); // Clear approved plan data
      setActiveUsers([]); // Clear active users
      return;
    }
    
    try {
      const planDoc = await getDoc(doc(db, 'saved_plans', planId));
      if (planDoc.exists()) {
        const plan = planDoc.data();
        // Ensure plan.data exists and has the expected structure
        if (plan.data && typeof plan.data === 'object') {
          setAssignedStaff(plan.data);
          setSelectedPlan(planId);
          setPreviouslySelectedPlan(planId); // Store this as the previously selected plan
          setApprovedPlanData(plan.data); // Store the plan data for Approved tab
          
          // Subscribe to real-time updates for this plan
          subscribeToPlan(planId);
          
          // Update user presence for collaborative editing
          updateUserPresence(planId, true);
          
          // Subscribe to user presence updates
          const presenceUnsubscribe = subscribeToPresence(planId);
          
          // Store presence unsubscriber
          setPlanListeners(prev => ({ 
            ...prev, 
            [`${planId}_presence`]: presenceUnsubscribe 
          }));
          
          const isExpiringSoon = isPlanCloseToExpiring(plan);
          const expiryMessage = isExpiringSoon ? ' ⚠️ Expires soon' : '';
          alert(`Plan "${plan.name}" loaded successfully!${expiryMessage}`);
        } else {
          console.error('Invalid plan data structure:', plan);
          alert('Invalid plan data. Please try loading a different plan.');
        }
      } else {
        alert('Plan not found. Please select a different plan.');
      }
    } catch (error) {
      console.error('Error loading plan:', error);
      alert('Failed to load plan. Please try again.');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!planId) {
      alert('Please select a plan to delete');
      return;
    }

    const planToDelete = savedPlans.find(plan => plan.id === planId);
    if (!planToDelete) {
      alert('Plan not found');
      return;
    }

    const confirmDelete = window.confirm(`Are you sure you want to delete the plan "${planToDelete.name}"? This action cannot be undone.`);
    if (!confirmDelete) {
      return;
    }

    try {
      await deletePlan(planId);
      
      // Remove from local state
      setSavedPlans(prevPlans => prevPlans.filter(plan => plan.id !== planId));
      
      // Clear selected plan if it was the deleted one
      if (selectedPlan === planId) {
        setSelectedPlan('');
        setAssignedStaff({
          A01: { westSideWaterspider: [], westToteRunner: [], westPA: [], eastSpider: [], eastPA: [], eastRunner: [], eastVRC: [] },
          A02: { westSideWaterspider: [], westToteRunner: [], westPA: [], eastSpider: [], eastPA: [] },
          A03: { westSideWaterspider: [], westToteRunner: [], westPA: [], eastSpider: [], eastPA: [] },
          A04: { westSideWaterspider: [], westToteRunner: [], westPA: [], eastSpider: [], eastPA: [] }
        });
      }
      
      // Clear previously selected plan if it was the deleted one
      if (previouslySelectedPlan === planId) {
        setPreviouslySelectedPlan('');
      }
      
      // Clear approved plan data if it was the deleted plan
      if (selectedPlan === planId) {
        setApprovedPlanData(null);
      }
      
      alert(`Plan "${planToDelete.name}" deleted successfully!`);
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert('Failed to delete plan. Please try again.');
    }
  };

  const handleUpdatePlan = async () => {
    if (!selectedPlan) {
      alert('Please select a plan to update');
      return;
    }

    const planToUpdate = savedPlans.find(plan => plan.id === selectedPlan);
    if (!planToUpdate) {
      alert('Selected plan not found');
      return;
    }

    const confirmUpdate = window.confirm(`Are you sure you want to update the plan "${planToUpdate.name}" with the current changes? This will overwrite the existing plan data.`);
    if (!confirmUpdate) {
      return;
    }

    try {
      console.log('Attempting to update plan:', planToUpdate.name);
      console.log('Current assignedStaff:', assignedStaff);
      
      await updatePlanRealtime(selectedPlan, assignedStaff, planToUpdate.name);
      
      alert(`Plan "${planToUpdate.name}" updated successfully!`);
    } catch (error) {
      console.error('Error updating plan:', error);
      alert(`Failed to update plan: ${error.message}. Please check console for details.`);
    }
  };

  // Real-time plan synchronization functions
  const updatePlanRealtime = async (planId, planData, planName) => {
    try {
      const planRef = doc(db, 'saved_plans', planId);
      
      // Get current plan to merge with updates
      const currentPlan = await getDoc(planRef);
      if (!currentPlan.exists()) {
        throw new Error('Plan not found');
      }

      const updatedPlan = {
        ...currentPlan.data(),
        data: { ...planData },
        updatedAt: serverTimestamp(),
        updatedBy: collaborativeUserId,
        lastModified: new Date().toISOString()
      };

      // Update in Firebase with server timestamp
      await setDoc(planRef, updatedPlan);
      console.log('Plan updated in Firebase successfully');
      
      // Update local state
      setSavedPlans(prevPlans => {
        const updated = prevPlans.map(plan => 
          plan.id === planId ? { ...updatedPlan, updatedAt: new Date() } : plan
        );
        savedPlansRef.current = updated; // Update ref for cleanup access
        return updated;
      });
      
      // Update approved plan data if this is the currently loaded plan
      if (selectedPlan === planId) {
        setApprovedPlanData({ ...planData });
      }
      
      setPendingChanges(false);
      setLastSyncTime(new Date());
      
    } catch (error) {
      console.error('Error in updatePlanRealtime:', error);
      throw error;
    }
  };

  // Subscribe to real-time plan updates
  const subscribeToPlan = (planId) => {
    if (planListeners[planId]) {
      // Already listening to this plan
      return;
    }

    console.log('Subscribing to real-time updates for plan:', planId);
    
    const planRef = doc(db, 'saved_plans', planId);
    const unsubscribe = onSnapshot(planRef, (doc) => {
      if (doc.exists()) {
        const planData = doc.data();
        console.log('Received real-time plan update:', planData);
        
        // Only update if the change wasn't made by current user
        if (planData.updatedBy !== collaborativeUserId) {
          console.log('Applying remote plan update from user:', planData.updatedBy);
          
          // Update assigned staff if this is the currently loaded plan
          if (selectedPlan === planId && planData.data) {
            setAssignedStaff(planData.data);
            setApprovedPlanData(planData.data);
          }
          
          // Update the plan in local state
          setSavedPlans(prevPlans => {
            const updated = prevPlans.map(plan => 
              plan.id === planId ? { ...planData, id: planId } : plan
            );
            savedPlansRef.current = updated; // Update ref for cleanup access
            return updated;
          });
          
          setLastSyncTime(new Date());
        }
      }
    }, (error) => {
      console.error('Error in plan subscription:', error);
      setIsOnline(false);
    });

    // Store the unsubscribe function
    setPlanListeners(prev => ({ ...prev, [planId]: unsubscribe }));
  };

  // Unsubscribe from plan updates
  const unsubscribeFromPlan = (planId) => {
    if (planListeners[planId]) {
      console.log('Unsubscribing from plan:', planId);
      planListeners[planId]();
      setPlanListeners(prev => {
        const { [planId]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  // Update user presence for collaborative editing
  const updateUserPresence = async (planId, isActive = true) => {
    try {
      const presenceRef = doc(db, 'plan_presence', `${planId}_${collaborativeUserId}`);
      
      if (isActive) {
        await setDoc(presenceRef, {
          userId: collaborativeUserId,
          planId: planId,
          lastSeen: serverTimestamp(),
          isActive: true
        });
      } else {
        await updateDoc(presenceRef, {
          isActive: false,
          lastSeen: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating user presence:', error);
    }
  };

  // Subscribe to user presence updates
  const subscribeToPresence = (planId) => {
    const presenceQuery = collection(db, 'plan_presence');
    
    return onSnapshot(presenceQuery, (snapshot) => {
      const users = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.planId === planId && data.isActive && data.userId !== collaborativeUserId) {
          // Only show users active in the last 2 minutes
          const lastSeen = data.lastSeen?.toDate() || new Date(0);
          const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000);
          
          if (lastSeen > twoMinutesAgo) {
            users.push(data);
          }
        }
      });
      setActiveUsers(users);
    });
  };

  const handleAssignStaffInPlan = async (area) => {
    if (selectedStaff.length === 0) {
      alert('Please select staff members to assign');
      return;
    }

    if (planMode === 'plan') {
      // Update planData for Plan tab
      const newPlanData = { ...planData };
      
      // Prevent direct assignment to West Tote Runner on A03 (auto-linked from A02/A04)
      if (area === 'westToteRunner' && selectedFloor === 'A03') {
        alert('West Tote Runner on A03 is auto-filled from A02/A04. Please assign on A02 or A04.');
        return;
      }
      
      // Add staff to the selected area on current floor
      // Ensure the area property exists before assignment
      if (!newPlanData[selectedFloor][area]) {
        newPlanData[selectedFloor][area] = [];
      }
      
      newPlanData[selectedFloor] = {
        ...newPlanData[selectedFloor],
        [area]: [...newPlanData[selectedFloor][area], ...selectedStaff]
      };

      // Optional linking for West PA: ask which empty floor to also assign to (user choice via modal)
      if (area === 'westPA') {
        const floorOrder = ['A01', 'A02', 'A03', 'A04'];
        const emptyFloors = floorOrder.filter(floor => (
          floor !== selectedFloor &&
          newPlanData[floor] &&
          Array.isArray(newPlanData[floor].westPA) &&
          newPlanData[floor].westPA.length === 0
        ));

        if (emptyFloors.length > 0) {
          setWestPAOptions(emptyFloors);
          setWestPAStaff(selectedStaff);
          setWestPAMode('plan');
          setShowWestPAModal(true);
        }
      }

      // Handle linked West Tote Runner assignments from A02/A04 to A03
      if (area === 'westToteRunner' && (selectedFloor === 'A02' || selectedFloor === 'A04')) {
        newPlanData['A03'] = {
          ...newPlanData['A03'],
          westToteRunner: [...newPlanData['A03'].westToteRunner, ...selectedStaff]
        };
      }

      // Handle linked East PA assignments between A03 and A04
      if (area === 'eastPA' && (selectedFloor === 'A03' || selectedFloor === 'A04')) {
        const linkedFloor = selectedFloor === 'A03' ? 'A04' : 'A03';
        newPlanData[linkedFloor] = {
          ...newPlanData[linkedFloor],
          eastPA: [...newPlanData[linkedFloor].eastPA, ...selectedStaff]
        };
      }

      // Handle linked East PA assignments between A01 and A02
      if (area === 'eastPA' && (selectedFloor === 'A01' || selectedFloor === 'A02')) {
        const linkedFloor = selectedFloor === 'A01' ? 'A02' : 'A01';
        newPlanData[linkedFloor] = {
          ...newPlanData[linkedFloor],
          eastPA: [...newPlanData[linkedFloor].eastPA, ...selectedStaff]
        };
      }

      setPlanData(newPlanData);
      setAssignedStaff(newPlanData);
    } else if (planMode === 'approved' && selectedPlan) {
      // Allow editing in Approved tab when a plan is selected
      const newAssignedStaff = { ...assignedStaff };
      
      // Prevent direct assignment to West Tote Runner on A03 (auto-linked from A02/A04)
      if (area === 'westToteRunner' && selectedFloor === 'A03') {
        alert('West Tote Runner on A03 is auto-filled from A02/A04. Please assign on A02 or A04.');
        return;
      }
      
      // Add staff to the selected area on current floor
      // Ensure the area property exists before assignment
      if (!newAssignedStaff[selectedFloor][area]) {
        newAssignedStaff[selectedFloor][area] = [];
      }
      
      newAssignedStaff[selectedFloor] = {
        ...newAssignedStaff[selectedFloor],
        [area]: [...newAssignedStaff[selectedFloor][area], ...selectedStaff]
      };

      // Optional linking for West PA: ask which empty floor to also assign to (user choice via modal)
      if (area === 'westPA') {
        const floorOrder = ['A01', 'A02', 'A03', 'A04'];
        const emptyFloors = floorOrder.filter(floor => (
          floor !== selectedFloor &&
          newAssignedStaff[floor] &&
          Array.isArray(newAssignedStaff[floor].westPA) &&
          newAssignedStaff[floor].westPA.length === 0
        ));

        if (emptyFloors.length > 0) {
          setWestPAOptions(emptyFloors);
          setWestPAStaff(selectedStaff);
          setWestPAMode('approved');
          setShowWestPAModal(true);
        }
      }

      // Handle linked West Tote Runner assignments from A02/A04 to A03
      if (area === 'westToteRunner' && (selectedFloor === 'A02' || selectedFloor === 'A04')) {
        newAssignedStaff['A03'] = {
          ...newAssignedStaff['A03'],
          westToteRunner: [...newAssignedStaff['A03'].westToteRunner, ...selectedStaff]
        };
      }

      // Handle linked East PA assignments between A03 and A04
      if (area === 'eastPA' && (selectedFloor === 'A03' || selectedFloor === 'A04')) {
        const linkedFloor = selectedFloor === 'A03' ? 'A04' : 'A03';
        newAssignedStaff[linkedFloor] = {
          ...newAssignedStaff[linkedFloor],
          eastPA: [...newAssignedStaff[linkedFloor].eastPA, ...selectedStaff]
        };
      }

      // Handle linked East PA assignments between A01 and A02
      if (area === 'eastPA' && (selectedFloor === 'A01' || selectedFloor === 'A02')) {
        const linkedFloor = selectedFloor === 'A01' ? 'A02' : 'A01';
        newAssignedStaff[linkedFloor] = {
          ...newAssignedStaff[linkedFloor],
          eastPA: [...newAssignedStaff[linkedFloor].eastPA, ...selectedStaff]
        };
      }

      setAssignedStaff(newAssignedStaff);
      // Update approved plan data when editing in Approved tab
      setApprovedPlanData(newAssignedStaff);
    } else {
      // For Approved tab without selected plan, this should not be allowed
      alert('Cannot modify assignments in Approved mode. Please use Plan tab to make changes or select a plan to edit.');
      return;
    }

    setSelectedStaff([]);
  };

  const handleUnassignStaffInPlan = async (staffToRemove, area) => {
    if (planMode === 'plan') {
      // Update planData for Plan tab
      const newPlanData = { ...planData };
      
      // Remove staff from the specified area on current floor
      // Ensure the area property exists before unassignment
      if (!newPlanData[selectedFloor][area]) {
        newPlanData[selectedFloor][area] = [];
      }
      
      newPlanData[selectedFloor][area] = newPlanData[selectedFloor][area].filter(
        staff => staff.login !== staffToRemove.login
      );

      // Handle linked West Tote Runner unassignments on A03 when source is A02/A04
      if (area === 'westToteRunner' && (selectedFloor === 'A02' || selectedFloor === 'A04')) {
        newPlanData['A03'].westToteRunner = newPlanData['A03'].westToteRunner.filter(
          staff => staff.login !== staffToRemove.login
        );
      }

      // Handle linked East PA unassignments between A03 and A04
      if (area === 'eastPA' && (selectedFloor === 'A03' || selectedFloor === 'A04')) {
        const linkedFloor = selectedFloor === 'A03' ? 'A04' : 'A03';
        newPlanData[linkedFloor].eastPA = newPlanData[linkedFloor].eastPA.filter(
          staff => staff.login !== staffToRemove.login
        );
      }

      // Handle linked East PA unassignments between A01 and A02
      if (area === 'eastPA' && (selectedFloor === 'A01' || selectedFloor === 'A02')) {
        const linkedFloor = selectedFloor === 'A01' ? 'A02' : 'A01';
        newPlanData[linkedFloor].eastPA = newPlanData[linkedFloor].eastPA.filter(
          staff => staff.login !== staffToRemove.login
        );
      }

      setPlanData(newPlanData);
      setAssignedStaff(newPlanData);
    } else if (planMode === 'approved' && selectedPlan) {
      // Allow editing in Approved tab when a plan is selected
      const newAssignedStaff = { ...assignedStaff };
      
      // Remove staff from the specified area on current floor
      // Ensure the area property exists before unassignment
      if (!newAssignedStaff[selectedFloor][area]) {
        newAssignedStaff[selectedFloor][area] = [];
      }
      
      newAssignedStaff[selectedFloor][area] = newAssignedStaff[selectedFloor][area].filter(
        staff => staff.login !== staffToRemove.login
      );

      // Handle linked West Tote Runner unassignments on A03 when source is A02/A04
      if (area === 'westToteRunner' && (selectedFloor === 'A02' || selectedFloor === 'A04')) {
        newAssignedStaff['A03'].westToteRunner = newAssignedStaff['A03'].westToteRunner.filter(
          staff => staff.login !== staffToRemove.login
        );
      }

      // Handle linked East PA unassignments between A03 and A04
      if (area === 'eastPA' && (selectedFloor === 'A03' || selectedFloor === 'A04')) {
        const linkedFloor = selectedFloor === 'A03' ? 'A04' : 'A03';
        newAssignedStaff[linkedFloor].eastPA = newAssignedStaff[linkedFloor].eastPA.filter(
          staff => staff.login !== staffToRemove.login
        );
      }

      // Handle linked East PA unassignments between A01 and A02
      if (area === 'eastPA' && (selectedFloor === 'A01' || selectedFloor === 'A02')) {
        const linkedFloor = selectedFloor === 'A01' ? 'A02' : 'A01';
        newAssignedStaff[linkedFloor].eastPA = newAssignedStaff[linkedFloor].eastPA.filter(
          staff => staff.login !== staffToRemove.login
        );
      }

      setAssignedStaff(newAssignedStaff);
      // Update approved plan data when editing in Approved tab
      setApprovedPlanData(newAssignedStaff);
    } else {
      // For Approved tab without selected plan, this should not be allowed
      alert('Cannot modify assignments in Approved mode. Please use Plan tab to make changes or select a plan to edit.');
      return;
    }
  };

  // Ticket creation functions
  const handleCreateTicket = async () => {
    if (!ticketForm.title.trim() || !ticketForm.description.trim()) {
      alert('Please fill in both title and description');
      return;
    }

    try {
      // Create ticket data with current staffing context
      const ticketData = {
        ...ticketForm,
        floor: selectedFloor,
        createdAt: new Date(),
        status: 'open',
        staffingContext: {
          currentFloor: selectedFloor,
          unassignedStaff: unassignedStaff.length,
          totalStaff: allStaff.length,
          assignedStaff: Object.values(currentFloorAssignedStaff).flat().length
        }
      };

      // Create ticket directly in Supabase database
      const createdTicket = await ticketService.createTicket(ticketData);
      
      // Reset form
      setTicketForm({
        title: '',
        description: '',
        priority: 'medium',
        category: 'SCC', // Reset to SCC
        subCategory: 'staffing', // Reset to staffing
        assignedTo: 'angel', // Reset to angel
        floor: selectedFloor
      });
      setShowTicketForm(false);
      
      alert('Ticket created successfully!');
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert(`Failed to create ticket: ${error.message}\n\nPlease check your Supabase configuration in src/supabase.js`);
    }
  };

  const handleTicketFormChange = (field, value) => {
    setTicketForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCancelTicketForm = () => {
    setTicketForm({
      title: '',
      description: '',
      priority: 'medium',
      category: 'SCC',
      subCategory: 'staffing',
      assignedTo: 'angel',
      floor: selectedFloor
    });
    setShowTicketForm(false);
  };

  // Quick ticket templates removed per request

  // Login functions
  const handleLogin = (email, password) => {
    setIsLoggedIn(true);
    setCurrentUser(email);
    setLoginEmail(email);
    setLoginPassword(password);
    setShowLoginForm(false);
    // Store login state in localStorage for persistence
    localStorage.setItem('staffingBoardUser', email);
    localStorage.setItem('staffingBoardEmail', email);
    localStorage.setItem('staffingBoardPassword', password);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentUser(null);
    setLoginEmail('');
    setLoginPassword('');
    localStorage.removeItem('staffingBoardUser');
    localStorage.removeItem('staffingBoardEmail');
    localStorage.removeItem('staffingBoardPassword');
  };

  // Check for existing login on app load
  useEffect(() => {
    const savedUser = localStorage.getItem('staffingBoardUser');
    const savedEmail = localStorage.getItem('staffingBoardEmail');
    const savedPassword = localStorage.getItem('staffingBoardPassword');
    if (savedUser) {
      setIsLoggedIn(true);
      setCurrentUser(savedUser);
      setLoginEmail(savedEmail || '');
      setLoginPassword(savedPassword || '');
    }
  }, []);

  // Dark mode toggle function
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('staffingBoardDarkMode', JSON.stringify(newDarkMode));
  };

  // Clock-in status update function
  const updateStaffStatusFromClockIn = (statusMap) => {
    setAllStaff(prevStaff => {
      return prevStaff.map(staff => {
        const newStatus = statusMap[staff.login];
        if (newStatus && newStatus !== staff.status) {
          console.log(`Updating ${staff.login} status from ${staff.status} to ${newStatus}`);
          return { ...staff, status: newStatus };
        }
        return staff;
      });
    });
  };

  // Update body background color when dark mode changes
  useEffect(() => {
    document.body.className = `${isDarkMode ? 'bg-gray-900' : 'bg-white'} overflow-x-hidden`;
  }, [isDarkMode]);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      // Cleanup all plan listeners
      Object.values(planListeners).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
      
      // Mark user as inactive for current plan
      if (selectedPlan) {
        updateUserPresence(selectedPlan, false);
      }
    };
  }, []);

  // Auto-sync when assignedStaff changes (for real-time collaboration)
  useEffect(() => {
    if (selectedPlan && selectedTab === 'Approved') {
      // Debounce the sync to avoid too many updates
      const timeoutId = setTimeout(() => {
        setPendingChanges(true);
        // Auto-sync after 3 seconds of inactivity
        const autoSyncTimeout = setTimeout(() => {
          if (pendingChanges) {
            updatePlanRealtime(selectedPlan, assignedStaff, 'Auto-sync')
              .catch(error => console.error('Auto-sync failed:', error));
          }
        }, 3000);
        
        return () => clearTimeout(autoSyncTimeout);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [assignedStaff, selectedPlan, selectedTab]);

  // Update user presence periodically
  useEffect(() => {
    if (selectedPlan) {
      const interval = setInterval(() => {
        updateUserPresence(selectedPlan, true);
      }, 30000); // Update every 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [selectedPlan]);

  // Listen for broadcast changes from Firebase
  useEffect(() => {
    // Listen to all saved plans to detect broadcast changes
    const plansRef = collection(db, 'saved_plans');
    
    const unsubscribe = onSnapshot(plansRef, (snapshot) => {
      let activeBroadcast = null;
      
      snapshot.forEach((doc) => {
        const planData = doc.data();
        if (planData.isBroadcasting) {
          activeBroadcast = {
            id: doc.id,
            name: planData.name,
            planDate: planData.planDate,
            shift: planData.shift,
            data: planData.data,
            createdAt: planData.createdAt,
            broadcastAt: planData.broadcastAt,
            status: 'broadcasting'
          };
        }
      });
      
      if (activeBroadcast) {
        setBroadcastPlan(activeBroadcast);
        setBroadcastStatus('broadcasting');
      } else {
        setBroadcastPlan(null);
        setBroadcastStatus('idle');
      }
    }, (error) => {
      console.error('Error listening to broadcast changes:', error);
      setBroadcastStatus('error');
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-white'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className={`mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{loadingStep}</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>This may take a few seconds on first load</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-white'} flex items-center justify-center`}>
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Connection Error</h2>
          <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{error}</p>
          <div className={`text-sm space-y-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <p>Please check:</p>
            <ul className="list-disc list-inside text-left">
              <li>Your internet connection</li>
              <li>Firebase project is active</li>
              <li>Firestore Database is enabled</li>
              <li>Security rules allow read/write access</li>
            </ul>
            <p className="mt-4">
              Check your Firebase configuration in <code>src/firebase.js</code>
            </p>
          </div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-200 overflow-x-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-white'} flex flex-col pb-12`} style={{height: '100vh'}}>
      {/* Top Header Bar */}
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-4 py-2 flex items-center justify-between transition-colors duration-200`}>
        <div className="flex items-center space-x-3">
          <div className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Staffing Command Center</div>
          {/* Dev Mode Indicator */}
          {isDevMode && (
            <div className="flex items-center space-x-1 bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium border border-orange-200">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span>DEV MODE</span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-4">
          {/* Status Indicators */}
          <div className="flex items-center space-x-4">
            {/* Clock-in Status Indicator */}
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isClockInPolling ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {isClockInPolling ? 'Live Status' : 'Status Offline'}
              </span>
              {clockInLastUpdate && (
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  ({new Date(clockInLastUpdate).toLocaleTimeString()})
                </span>
              )}
            </div>

            {/* Collaborative Editing Status */}
            {selectedPlan && (selectedTab === 'Approved' || selectedTab === 'Plan') && (
              <>
                {/* Sync Status */}
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    pendingChanges ? 'bg-yellow-500' : 
                    lastSyncTime ? 'bg-green-500' : 'bg-gray-400'
                  }`}></div>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {pendingChanges ? 'Syncing...' : 
                     lastSyncTime ? 'Synced' : 'Offline'}
                  </span>
                  {lastSyncTime && (
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {lastSyncTime.toLocaleTimeString()}
                    </span>
                  )}
                </div>

                {/* Active Users */}
                {activeUsers.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <div className="flex -space-x-1">
                      {activeUsers.slice(0, 3).map((user, index) => (
                        <div
                          key={user.userId}
                          className={`w-6 h-6 rounded-full border-2 ${
                            isDarkMode ? 'border-gray-800' : 'border-white'
                          } flex items-center justify-center text-xs font-medium text-white`}
                          style={{ 
                            backgroundColor: `hsl(${(user.userId.charCodeAt(0) * 137.5) % 360}, 70%, 50%)`,
                            zIndex: 10 - index
                          }}
                          title={`User ${user.userId} is editing`}
                        >
                          {user.userId.charAt(0).toUpperCase()}
                        </div>
                      ))}
                      {activeUsers.length > 3 && (
                        <div className={`w-6 h-6 rounded-full border-2 ${
                          isDarkMode ? 'border-gray-800 bg-gray-700' : 'border-white bg-gray-300'
                        } flex items-center justify-center text-xs font-medium ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          +{activeUsers.length - 3}
                        </div>
                      )}
                    </div>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {activeUsers.length} editing
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Login/User Section */}
          <div className="flex items-center space-x-2">
            {isLoggedIn ? (
              <>
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Welcome, {currentUser ? String(currentUser).split('@')[0] : ''}
                </span>
                {/* Floor and Area selectors placed after welcome text */}
                <select 
                  className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                  value={selectedFloor}
                  onChange={(e) => setSelectedFloor(e.target.value)}
                >
                  <option value="A01">A01</option>
                  <option value="A02">A02</option>
                  <option value="A03">A03</option>
                  <option value="A04">A04</option>
                </select>
                <select className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}>
                  <option>RSP</option>
                </select>
                <button
                  onClick={handleLogout}
                  className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                {/* Keep floor/area selectors visible even when not logged in */}
                <select 
                  className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                  value={selectedFloor}
                  onChange={(e) => setSelectedFloor(e.target.value)}
                >
                  <option value="A01">A01</option>
                  <option value="A02">A02</option>
                  <option value="A03">A03</option>
                  <option value="A04">A04</option>
                </select>
                <select className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}>
                  <option>RSP</option>
                </select>
                <button
                  onClick={() => setShowLoginForm(true)}
                  className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 transition-colors"
                >
                  Login
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Cleanup Notification */}
      {cleanupNotification && (
        <div className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg transition-all duration-300 ${
          cleanupNotification.type === 'success' 
            ? 'bg-green-500 text-white' 
            : 'bg-red-500 text-white'
        }`}>
          <div className="flex items-center space-x-2">
            <span>{cleanupNotification.message}</span>
            <button
              onClick={() => setCleanupNotification(null)}
              className="ml-2 text-white hover:text-gray-200"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b transition-colors duration-200`}>
        <div className="flex items-center space-x-8 px-4">
          <button
            onClick={() => handleTabChange('Cut us a ticket')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors focus:outline-none focus:ring-0 focus-visible:outline-none ${
              selectedTab === 'Cut us a ticket'
                ? 'border-blue-500 text-blue-600'
                : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            Cut us a ticket
          </button>
          
          <div className={`font-medium ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>|</div>
          
          <button
            onClick={() => handleTabChange('Approved')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors focus:outline-none focus:ring-0 focus-visible:outline-none ${
              selectedTab === 'Approved'
                ? 'border-blue-500 text-blue-600'
                : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            Approved
          </button>
          
          <button
            onClick={() => handleTabChange('Plan')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors focus:outline-none focus:ring-0 focus-visible:outline-none ${
              selectedTab === 'Plan'
                ? 'border-blue-500 text-blue-600'
                : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            Plan
          </button>
        </div>
      </div>

      {/* West PA additional assignment modal */}
      {showWestPAModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black opacity-50" onClick={() => { setShowWestPAModal(false); setWestPASelection(''); setWestPAOptions([]); setWestPAStaff([]); }}></div>
          <div className={`relative rounded-lg shadow-xl w-full max-w-sm p-4 z-10 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
            <h3 className="text-sm font-semibold mb-2">Also assign to West PA on another floor?</h3>
            <p className="text-xs mb-3">Select a floor that currently has an empty West PA:</p>
            <select
              className={`w-full border rounded px-2 py-1 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
              value={westPASelection}
              onChange={(e) => setWestPASelection(e.target.value)}
            >
              <option value="" disabled>Select floor</option>
              {westPAOptions.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <div className="flex justify-end space-x-2 mt-4">
              <button
                className={`px-3 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                onClick={() => { setShowWestPAModal(false); setWestPASelection(''); setWestPAOptions([]); setWestPAStaff([]); }}
              >
                Cancel
              </button>
              <button
                className={`px-3 py-1 rounded text-sm ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                onClick={() => {
                  if (!westPASelection) { setShowWestPAModal(false); return; }
                  if (westPAMode === 'plan') {
                    const npd = { ...planData };
                    const existing = new Set((npd[westPASelection].westPA || []).map(s => s.login));
                    const toAdd = westPAStaff.filter(s => !existing.has(s.login));
                    npd[westPASelection] = {
                      ...npd[westPASelection],
                      westPA: [...npd[westPASelection].westPA, ...toAdd]
                    };
                    setPlanData(npd);
                    setAssignedStaff(npd);
                  } else {
                    const nas = { ...assignedStaff };
                    const existing = new Set((nas[westPASelection].westPA || []).map(s => s.login));
                    const toAdd = westPAStaff.filter(s => !existing.has(s.login));
                    nas[westPASelection] = {
                      ...nas[westPASelection],
                      westPA: [...nas[westPASelection].westPA, ...toAdd]
                    };
                    setAssignedStaff(nas);
                    setApprovedPlanData(nas);
                  }
                  setShowWestPAModal(false);
                  setWestPASelection('');
                  setWestPAOptions([]);
                  setWestPAStaff([]);
                }}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="p-4 flex-1 min-h-0 flex flex-col">
        {/* Top Section */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <h1 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>RSP Staffing</h1>
            {selectedTab === 'Approved' && (
              <div className="flex items-center space-x-2">
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Plan:</span>
                <select 
                  className={`border rounded px-3 py-1 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                  value={selectedPlan}
                  onChange={(e) => handleLoadPlan(e.target.value)}
                >
                  <option value="">Select a plan...</option>
                  {savedPlans.map(plan => {
                    const isExpiringSoon = isPlanCloseToExpiring(plan);
                    
                    // Safely get the creation date
                    let creationDate = 'Unknown';
                    try {
                      if (plan.createdAt) {
                        const convertedDate = convertTimestamp(plan.createdAt);
                        if (convertedDate instanceof Date && !isNaN(convertedDate.getTime())) {
                          creationDate = convertedDate.toLocaleDateString();
                        }
                      }
                    } catch (error) {
                      console.warn('Error converting plan creation date:', error);
                    }
                    
                    return (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} (Created: {creationDate})
                        {isExpiringSoon ? ' ⚠️' : ''}
                      </option>
                    );
                  })}
                </select>
                {selectedPlan && (
                  <>
                    <button
                      onClick={() => pushToBroadcast(selectedPlan)}
                      className={`px-3 py-1 rounded text-sm mr-2 ${
                        broadcastPlan && broadcastPlan.id === selectedPlan
                          ? 'bg-green-500 text-white hover:bg-green-600'
                          : 'bg-purple-500 text-white hover:bg-purple-600'
                      }`}
                      title={
                        broadcastPlan && broadcastPlan.id === selectedPlan
                          ? 'This plan is currently being broadcasted'
                          : 'Push this plan to all broadcast views'
                      }
                    >
                      {broadcastPlan && broadcastPlan.id === selectedPlan ? 'Broadcasting' : 'Push to Broadcast'}
                    </button>
                    <button
                      onClick={() => handleDeletePlan(selectedPlan)}
                      className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                      title="Delete selected plan"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          

          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-800'}>At Station (LEO)</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-green-300 rounded-full"></div>
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-800'}>At Station (Override)</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-800'}>Clocked-In</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-800'}>Not Clocked-In</span>
              </div>
            </div>
            <button 
              onClick={handleResetToDefault}
              className="bg-blue-500 text-white px-4 py-1 rounded text-sm hover:bg-blue-600"
              title="Reset to default staff and clear all assignments"
            >
              Replan
            </button>
            <button 
              onClick={selectedTab === 'Plan' ? handleSavePlan : handleUpdatePlan}
              className={`px-4 py-1 rounded text-sm ${
                selectedTab === 'Plan' 
                  ? 'bg-green-500 text-white hover:bg-green-600' 
                  : selectedTab === 'Approved' && selectedPlan
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-gray-300 text-gray-600 disabled:opacity-50'
              }`}
              disabled={selectedTab === 'Plan' ? false : (selectedTab === 'Approved' ? !selectedPlan : true)}
              title={
                selectedTab === 'Plan' 
                  ? 'Save current plan' 
                  : selectedTab === 'Approved' && selectedPlan
                    ? 'Update selected plan with current changes'
                    : 'Only available in Plan mode or when a plan is selected in Approved mode'
              }
            >
              {selectedTab === 'Plan' ? 'Review' : (selectedTab === 'Approved' && selectedPlan ? 'Update Plan' : 'Review')}
            </button>
          </div>
        </div>

        {/* Main Content Based on Selected Tab */}
        {selectedTab === 'Cut us a ticket' ? (
          // Ticket Creation Interface
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg p-6 transition-colors duration-200`}>
            <div className="mb-6">
              <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Create Support Ticket</h2>
              <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Create tickets for staffing issues, operational problems, or general support requests.
              </p>
              
              {/* Quick Ticket Templates removed */}

              {/* Ticket Form */}
              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Title</label>
                    <input
                      type="text"
                      value={ticketForm.title}
                      onChange={(e) => handleTicketFormChange('title', e.target.value)}
                      className={`w-full rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-800 border border-gray-600 text-gray-100 placeholder-gray-400' : 'border border-gray-300'}`}
                      placeholder="Enter ticket title..."
                    />
                  </div>
                                      <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Priority</label>
                      <select
                        value={ticketForm.priority}
                        onChange={(e) => handleTicketFormChange('priority', e.target.value)}
                        className={`w-full rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-800 border border-gray-600 text-gray-100' : 'border border-gray-300'}`}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Category</label>
                    <select
                      value={ticketForm.category}
                      onChange={(e) => handleTicketFormChange('category', e.target.value)}
                      className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-400' : 'border-gray-300 bg-gray-100'}`}
                      disabled
                    >
                      <option value="SCC">SCC</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Sub-Category</label>
                    <select
                      value={ticketForm.subCategory}
                      onChange={(e) => handleTicketFormChange('subCategory', e.target.value)}
                      className={`w-full rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-800 border border-gray-600 text-gray-100' : 'border border-gray-300'}`}
                    >
                      <option value="staffing">Staffing</option>
                      <option value="operations">Operations</option>
                      <option value="technical">Technical</option>
                      <option value="general">General</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Assigned To</label>
                    <input
                      type="text"
                      value={ticketForm.assignedTo}
                      className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-400' : 'border-gray-300 bg-gray-100'}`}
                      placeholder="angel"
                      readOnly
                      disabled
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Description</label>
                  <textarea
                    value={ticketForm.description}
                    onChange={(e) => handleTicketFormChange('description', e.target.value)}
                    rows={4}
                    className={`w-full rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-800 border border-gray-600 text-gray-100 placeholder-gray-400' : 'border border-gray-300'}`}
                    placeholder="Describe the issue or request..."
                  />
                </div>

                <div className="flex justify-between items-center">
                  <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Floor: {selectedFloor} | Unassigned Staff: {unassignedStaff.length}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCancelTicketForm}
                      className="bg-gray-500 text-white px-4 py-2 rounded text-sm hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateTicket}
                      className="bg-blue-500 text-white px-4 py-2 rounded text-sm hover:bg-blue-600"
                    >
                      Create Ticket
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : selectedTab === 'Approved' && !selectedPlan ? (
          // No Plan Selected Message for Approved Tab
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg p-8 transition-colors duration-200`}>
            <div className="text-center">
              <div className={`${isDarkMode ? 'text-gray-500' : 'text-gray-400'} mb-4`}>
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h2 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>No Plan Selected</h2>
              <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Please select a plan from the dropdown above to view and edit its contents.
              </p>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Available plans: {savedPlans.length}
                {savedPlans.length > 0 && (
                  <button
                    onClick={async () => {
                      if (window.confirm('Manually trigger cleanup of expired plans?')) {
                        await cleanupExpiredPlans(savedPlans);
                      }
                    }}
                    className="ml-2 bg-orange-500 text-white px-2 py-1 rounded text-xs hover:bg-orange-600"
                    title="Manually clean up expired plans"
                  >
                    Cleanup
                  </button>
                )}
                {planCleanupIntervalRef.current && (
                  <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Next cleanup: {new Date(Date.now() + 60 * 60 * 1000).toLocaleTimeString()}
                  </div>
                )}
                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Plans auto-delete after 24 hours
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Original Staffing Interface
          <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 overflow-x-hidden">
            {/* Left Panel - Unassigned Staff (only this side allows horizontal scroll) */}
            <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg transition-colors duration-200 overflow-x-auto h-full flex flex-col`}>
              <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex">
                  {['Unassigned', 'Global'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setSelectedUnassignedTab(tab)}
                      className={`px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-0 focus-visible:outline-none ${
                        selectedUnassignedTab === tab
                          ? 'text-blue-600 border-b-2 border-blue-500'
                          : `${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>
            
            <div className="p-4 h-full flex flex-col min-h-0">
              <div className="flex justify-between items-center mb-3">
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {selectedUnassignedTab === 'Unassigned' 
                    ? `Unassigned: ${searchTerm ? `${filteredUnassignedStaff.length}/${unassignedStaff.length}` : unassignedStaff.length}` 
                    : `Global: ${searchTerm ? `${getSortedGlobalStaff().length}/${allStaff.length}` : allStaff.length}${globalSortByAssignment ? ' (Sorted by Assignment)' : ''}`
                  }
                </span>
                {selectedUnassignedTab === 'Unassigned' && (
                  <div className="flex space-x-2">
                    <button 
                      onClick={handleResetToDefault}
                      className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                      title="Reset to default staff list"
                    >
                      Reset
                    </button>
                    {canAddStaff ? (
                      <>
                        <button 
                          onClick={() => setShowAddForm(!showAddForm)}
                          className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                        >
                          {showAddForm ? 'Cancel' : 'Add Staff'}
                        </button>
                        <button 
                          onClick={() => setShowCsvImport(!showCsvImport)}
                          className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                          title="Bulk import employee mappings from CSV"
                        >
                          CSV Import
                        </button>
                      </>
                    ) : (
                      <div className={`text-xs px-2 py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {!isLoggedIn ? 'Login to add staff' : 'Insufficient permissions to add staff'}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Add Staff Form - Only show in Unassigned tab */}
              {selectedUnassignedTab === 'Unassigned' && showAddForm && (
                <div className={`mb-4 p-3 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Login"
                      value={newStaff.login}
                      onChange={(e) => setNewStaff({...newStaff, login: e.target.value})}
                      className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                    />
                    <input
                      type="text"
                      className={`border rounded px-3 py-2 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                      placeholder="Role"
                      value={newStaff.role}
                      onChange={(e) => setNewStaff({...newStaff, role: e.target.value})}
                    />
                    <select
                      value={newStaff.status}
                      onChange={(e) => setNewStaff({...newStaff, status: e.target.value})}
                      className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                    >
                      <option value="red">Not Clocked-In</option>
                      <option value="orange">Clocked-In</option>
                      <option value="green">At Station (LEO)</option>
                      <option value="light-green">At Station (Override)</option>
                    </select>
                  </div>
                  <button 
                    onClick={handleAddStaff}
                    className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                  >
                    Add
                  </button>
                </div>
              )}

              {/* CSV Import Form - Only show in Unassigned tab for admin users */}
              {selectedUnassignedTab === 'Unassigned' && showCsvImport && canAddStaff && (
                <div className={`mb-4 p-4 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      📊 Bulk Staff & Employee Mapping Import
                    </h3>
                    <button
                      onClick={() => setShowCsvImport(false)}
                      className={`text-gray-500 hover:text-gray-700 ${isDarkMode ? 'hover:text-gray-300' : ''}`}
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className={`mb-3 p-3 rounded ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-blue-50 border-blue-200'} border`}>
                    <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                      📋 CSV Format Requirements:
                    </h4>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-blue-700'} space-y-1`}>
                      <div>• <strong>Required columns:</strong> login, employeeid</div>
                      <div>• <strong>Optional columns:</strong> role, status</div>
                      <div>• <strong>Header row:</strong> First row must contain column names</div>
                      <div>• <strong>Employee ID:</strong> Must be numeric (6+ digits)</div>
                      <div>• <strong>Login:</strong> Staff login/username (case insensitive)</div>
                      <div>• <strong>Role:</strong> Staff role/position (optional)</div>
                      <div>• <strong>Status:</strong> red, orange, green, light-green (defaults to red)</div>
                      <div className={`mt-2 p-2 rounded ${isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-50 text-green-700'}`}>
                        <strong>✨ New Feature:</strong> CSV import now creates both employee mappings AND staff records!
                      </div>
                      <div className="mt-2 font-mono text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded">
                        Example CSV (with optional columns):<br/>
                        login,employeeid,role,status<br/>
                        acarreom,201876229,Manager,green<br/>
                        jsmith,201234567,Associate,red<br/>
                        mdoe,201345678,PA,orange
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        Select CSV File:
                      </label>
                      <input
                        id="csvFileInput"
                        type="file"
                        accept=".csv"
                        onChange={handleCsvFileSelect}
                        className={`block w-full text-sm ${
                          isDarkMode 
                            ? 'text-gray-300 file:bg-gray-600 file:text-white file:border-gray-500' 
                            : 'text-gray-500 file:bg-gray-50 file:text-gray-700 file:border-gray-300'
                        } file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold hover:file:bg-gray-100 dark:hover:file:bg-gray-700`}
                      />
                    </div>

                    {csvImportStatus && (
                      <div className={`p-3 rounded ${
                        csvImportStatus.includes('failed') || csvImportStatus.includes('error') 
                          ? isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-50 text-red-700'
                          : csvImportStatus.includes('complete')
                          ? isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-50 text-green-700'
                          : isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-700'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">{csvImportStatus}</span>
                          {csvImportProgress > 0 && csvImportProgress < 100 && (
                            <span className="text-xs">{csvImportProgress}%</span>
                          )}
                        </div>
                        {csvImportProgress > 0 && csvImportProgress < 100 && (
                          <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${csvImportProgress}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                    )}

                    {csvImportResults && (
                      <div className={`p-3 rounded border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                        <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          Import Results:
                        </h4>
                        <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} space-y-1`}>
                          <div>📊 Total Records: {csvImportResults.total}</div>
                          <div className="text-green-600">✅ Successful: {csvImportResults.successful}</div>
                          <div className="text-red-600">❌ Failed: {csvImportResults.failed}</div>
                          
                          <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
                            <div className="text-blue-600">👥 Staff Created: {csvImportResults.staffCreated || 0}</div>
                            <div className="text-yellow-600">🔄 Staff Updated: {csvImportResults.staffUpdated || 0}</div>
                            <div className="text-purple-600">🗂️ Mappings Created: {csvImportResults.mappingCreated || 0}</div>
                          </div>
                          
                          {csvImportResults.errors.length > 0 && (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-red-600 hover:text-red-800">
                                View Errors ({csvImportResults.errors.length})
                              </summary>
                              <div className="mt-2 max-h-32 overflow-y-auto">
                                {csvImportResults.errors.map((error, index) => (
                                  <div key={index} className="text-xs text-red-600 mb-1">
                                    Row {error.row}: {error.login} - {error.error}
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex space-x-2">
                      <button
                        onClick={handleCsvImport}
                        disabled={!csvFile || csvImportProgress > 0}
                        className={`px-4 py-2 rounded text-sm font-medium ${
                          !csvFile || csvImportProgress > 0
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                      >
                        {csvImportProgress > 0 && csvImportProgress < 100 ? 'Importing...' : 'Import CSV'}
                      </button>
                      
                      <button
                        onClick={() => {
                          setShowCsvImport(false);
                          setCsvFile(null);
                          setCsvImportStatus('');
                          setCsvImportProgress(0);
                          setCsvImportResults(null);
                          if (document.getElementById('csvFileInput')) {
                            document.getElementById('csvFileInput').value = '';
                          }
                        }}
                        className={`px-4 py-2 rounded text-sm font-medium ${
                          isDarkMode 
                            ? 'bg-gray-600 text-white hover:bg-gray-700' 
                            : 'bg-gray-500 text-white hover:bg-gray-600'
                        }`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Search and filter section */}
              <div className="mb-4">
                <div className="flex items-center space-x-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder="Search by login..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`w-full rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-800 border border-gray-600 text-gray-100 placeholder-gray-400' : 'border border-gray-300'}`}
                    />
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => setShowRoleFilter(!showRoleFilter)}
                      className={`p-2 rounded border ${
                        selectedRoleFilter
                          ? (isDarkMode
                              ? 'bg-blue-600 text-white border-blue-500 hover:bg-blue-700'
                              : 'bg-blue-100 border-blue-300 text-blue-600 hover:bg-blue-200')
                          : (isDarkMode
                              ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                              : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100')
                      }`}
                      title="Filter by role"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    </button>
                    
                    {/* Role filter dropdown */}
                    {showRoleFilter && (
                      <div className={`role-filter-container absolute right-0 mt-1 w-48 ${isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-700'} border rounded shadow-lg z-10 transition-colors duration-200`}>
                        <div className="p-2">
                          <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Filter by Role</div>
                          <button
                            onClick={() => {
                              setSelectedRoleFilter('');
                              setShowRoleFilter(false);
                            }}
                            className={`w-full text-left px-2 py-1 text-sm rounded ${
                              selectedRoleFilter === ''
                                ? (isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600')
                                : (isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'hover:bg-gray-100')
                            }`}
                          >
                            All Roles
                          </button>
                          {getUniqueRoles().map(role => (
                            <button
                              key={role}
                              onClick={() => {
                                setSelectedRoleFilter(role);
                                setShowRoleFilter(false);
                              }}
                              className={`w-full text-left px-2 py-1 text-sm rounded ${
                                selectedRoleFilter === role
                                  ? (isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600')
                                  : (isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'hover:bg-gray-100')
                              }`}
                            >
                              {role}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {(searchTerm || selectedRoleFilter) && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedRoleFilter('');
                      }}
                      className={`text-sm ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {(searchTerm || selectedRoleFilter) && (
                  <div className={`mt-2 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {searchTerm && <span>Search: "{searchTerm}"</span>}
                    {searchTerm && selectedRoleFilter && <span> • </span>}
                    {selectedRoleFilter && <span>Role: "{selectedRoleFilter}"</span>}
                  </div>
                )}
              </div>

              <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} transition-colors duration-200`}>
                <div className={`px-3 py-2 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} transition-colors duration-200`}>
                  <div className={`min-w-[700px] grid grid-cols-4 gap-4 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>
                    <span>Login</span>
                    <span>Role</span>
                    <span>Status</span>
                    <span>
                      {selectedUnassignedTab === 'Unassigned' ? 'Actions' : (
                        <button 
                          onClick={handleAssignmentSort}
                          className={`flex items-center space-x-1 hover:${isDarkMode ? 'text-gray-100' : 'text-gray-800'} transition-colors duration-200`}
                          title={globalSortByAssignment ? 'Click to remove sorting' : 'Click to sort by assignment status'}
                        >
                          <span>Assignment</span>
                          {globalSortByAssignment && (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      )}
                    </span>
                  </div>
                </div>
                
                <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0">
                  {(selectedUnassignedTab === 'Unassigned' ? filteredUnassignedStaff : getSortedGlobalStaff()).map((staff) => (
                    <div
                      key={staff.id}
                                              className={`px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${
                        selectedStaff.find(s => s.login === staff.login) ? (isDarkMode ? 'bg-blue-900' : 'bg-blue-50') : ''
                      } ${globalSortByAssignment && selectedUnassignedTab === 'Global' ? (
                        isStaffAssigned(staff.login) 
                          ? (isDarkMode ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-green-400') 
                          : (isDarkMode ? 'border-l-4 border-l-gray-500' : 'border-l-4 border-l-gray-300')
                      ) : ''}`}
                      onClick={() => (selectedUnassignedTab === 'Unassigned' || switchMode) && handleStaffSelect(staff)}
                      style={{ cursor: (selectedUnassignedTab === 'Unassigned' || switchMode) ? 'pointer' : 'default' }}
                    >
                      {selectedUnassignedTab === 'Unassigned' && editingId === staff.id ? (
                        // Edit mode - Only in Unassigned tab
                        <div className="min-w-[700px] grid grid-cols-4 gap-4 text-sm">
                          <input
                            type="text"
                            defaultValue={staff.login}
                            className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                            onBlur={(e) => handleSaveEdit(staff.id, { login: e.target.value })}
                          />
                          <input
                            type="text"
                            defaultValue={staff.role}
                            className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                            onBlur={(e) => handleSaveEdit(staff.id, { role: e.target.value })}
                          />
                          <select
                            defaultValue={staff.status}
                            className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                            onChange={(e) => handleSaveEdit(staff.id, { status: e.target.value })}
                          >
                            <option value="red">Not Clocked-In</option>
                            <option value="orange">Clocked-In</option>
                            <option value="green">At Station (LEO)</option>
                            <option value="light-green">At Station (Override)</option>
                          </select>
                          <div className="flex space-x-1">
                            <button 
                              onClick={() => handleCancelEdit()}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="min-w-[700px] grid grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                            </svg>
                            <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              {staff.login}
                            </span>
                            {/* FCLM Redirect Button */}
                            <div className="relative group">
                              <button
                                className={`p-1 rounded hover:bg-gray-200 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFCLMRedirect(staff.login);
                                }}
                              >
                                <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </button>
                              {/* Tooltip */}
                              <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap ${
                                isDarkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-gray-900 text-white'
                              }`}>
                                Open in FCLM
                                <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                                  isDarkMode ? 'border-t-gray-800' : 'border-t-gray-900'
                                }`}></div>
                              </div>
                            </div>
                          </div>
                          <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>{staff.role || '--'}</div>
                          <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full ${
                              staff.status === 'red' ? 'bg-red-500' : 
                              staff.status === 'orange' ? 'bg-orange-500' :
                              staff.status === 'green' ? 'bg-green-500' : 'bg-green-300'
                            }`}></div>
                            <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                              {staff.status === 'red' ? 'Not Clocked-In' :
                               staff.status === 'orange' ? 'Clocked-In' :
                               staff.status === 'green' ? 'At Station (LEO)' : 'At Station (Override)'}
                            </span>
                          </div>
                          <div className="flex space-x-2">
                            {selectedUnassignedTab === 'Unassigned' ? (
                              // Actions for Unassigned tab
                              <>
                                {canAddStaff ? (
                                  <>
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStartEdit(staff);
                                      }}
                                      className="text-blue-500 hover:text-blue-700 text-sm"
                                    >
                                      Edit
                                    </button>
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteStaff(staff.id);
                                      }}
                                      className="text-red-500 hover:text-red-700 text-sm"
                                    >
                                      Delete
                                    </button>
                                  </>
                                ) : (
                                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {!isLoggedIn ? 'Login to edit' : 'Insufficient permissions'}
                                  </span>
                                )}
                              </>
                            ) : (
                              // Assignment status for Global tab
                              <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                                {(() => {
                                  // Special handling for East PA to check both A03 and A04
                                  const isOnA03EastPA = assignedStaff.A03 && assignedStaff.A03.eastPA && 
                                    assignedStaff.A03.eastPA.find(assigned => assigned.login === staff.login);
                                  const isOnA04EastPA = assignedStaff.A04 && assignedStaff.A04.eastPA && 
                                    assignedStaff.A04.eastPA.find(assigned => assigned.login === staff.login);
                                  
                                  // Special handling for East PA to check both A01 and A02
                                  const isOnA01EastPA = assignedStaff.A01 && assignedStaff.A01.eastPA && 
                                    assignedStaff.A01.eastPA.find(assigned => assigned.login === staff.login);
                                  const isOnA02EastPA = assignedStaff.A02 && assignedStaff.A02.eastPA && 
                                    assignedStaff.A02.eastPA.find(assigned => assigned.login === staff.login);
                                  
                                  // Special handling for West Tote Runner linkages (A02/A04 linked to A03)
                                  const isOnA02WTR = assignedStaff.A02 && assignedStaff.A02.westToteRunner &&
                                    assignedStaff.A02.westToteRunner.find(assigned => assigned.login === staff.login);
                                  const isOnA03WTR = assignedStaff.A03 && assignedStaff.A03.westToteRunner &&
                                    assignedStaff.A03.westToteRunner.find(assigned => assigned.login === staff.login);
                                  const isOnA04WTR = assignedStaff.A04 && assignedStaff.A04.westToteRunner &&
                                    assignedStaff.A04.westToteRunner.find(assigned => assigned.login === staff.login);

                                  if (isOnA03EastPA && isOnA04EastPA) {
                                    return 'East PA on A03 & A04';
                                  }
                                  
                                  if (isOnA01EastPA && isOnA02EastPA) {
                                    return 'East PA on A01 & A02';
                                  }

                                  // West Tote Runner combined display
                                  if (isOnA02WTR && isOnA03WTR) {
                                    return 'West Tote Runner on A02 & A03';
                                  }
                                  if (isOnA04WTR && isOnA03WTR) {
                                    return 'West Tote Runner on A04 & A03';
                                  }

                                  // West PA combined display (generalized to any multi-floor combination)
                                  const wpaFloors = ['A01','A02','A03','A04'].filter(f =>
                                    assignedStaff[f] && assignedStaff[f].westPA &&
                                    assignedStaff[f].westPA.find(assigned => assigned.login === staff.login)
                                  );
                                  if (wpaFloors.length >= 2) {
                                    const label = wpaFloors.length === 2
                                      ? `${wpaFloors[0]} & ${wpaFloors[1]}`
                                      : `${wpaFloors.slice(0, -1).join(', ')} & ${wpaFloors[wpaFloors.length - 1]}`;
                                    return `West PA on ${label}`;
                                  }
                                  
                                  // Check all other assignments
                                  for (const floor of Object.keys(assignedStaff)) {
                                    for (const area of Object.keys(assignedStaff[floor])) {
                                      if (assignedStaff[floor][area].find(assigned => assigned.login === staff.login)) {
                                        const areaDisplayName = getAreaDisplayName(area, floor);
                                        
                                        // For East PA, show single floor if not linked
                                        if (area === 'eastPA' && (floor === 'A03' || floor === 'A04')) {
                                          if (isOnA03EastPA && !isOnA04EastPA) {
                                            return 'East PA on A03';
                                          } else if (isOnA04EastPA && !isOnA03EastPA) {
                                            return 'East PA on A04';
                                          }
                                        }
                                        
                                        if (area === 'eastPA' && (floor === 'A01' || floor === 'A02')) {
                                          if (isOnA01EastPA && !isOnA02EastPA) {
                                            return 'East PA on A01';
                                          } else if (isOnA02EastPA && !isOnA01EastPA) {
                                            return 'East PA on A02';
                                          }
                                        }
                                        
                                        // For West Tote Runner, if only one floor present show single
                                        if (area === 'westToteRunner') {
                                          if (floor === 'A02' && isOnA02WTR && !isOnA03WTR) {
                                            return 'West Tote Runner on A02';
                                          }
                                          if (floor === 'A04' && isOnA04WTR && !isOnA03WTR) {
                                            return 'West Tote Runner on A04';
                                          }
                                          if (floor === 'A03' && isOnA03WTR && !isOnA02WTR && !isOnA04WTR) {
                                            return 'West Tote Runner on A03';
                                          }
                                        }

                                        // For West PA, show single floor if only one present
                                        if (area === 'westPA') {
                                          const singleWpaFloors = ['A01','A02','A03','A04'].filter(f =>
                                            assignedStaff[f] && assignedStaff[f].westPA &&
                                            assignedStaff[f].westPA.find(assigned => assigned.login === staff.login)
                                          );
                                          if (singleWpaFloors.length === 1) {
                                            return `West PA on ${singleWpaFloors[0]}`;
                                          }
                                        }
                                        
                                        return `${areaDisplayName} on ${floor}`;
                                      }
                                    }
                                  }
                                  return 'Unassigned';
                                })()}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Selection info - Only show in Unassigned tab or when switch mode is active */}
              {(selectedUnassignedTab === 'Unassigned' || switchMode) && (
                <div className="flex justify-between items-center mt-3">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {switchMode ? (
                      <span>
                        Switching: <span className="font-semibold text-blue-600">{switchModeStaff?.login}</span>
                      </span>
                    ) : (
                      `${selectedStaff.length} selected`
                    )}
                  </span>
                  <button 
                    onClick={() => {
                      setSelectedStaff([]);
                      setSwitchMode(false);
                      setSwitchModeStaff(null);
                    }}
                    className={`text-sm ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                    disabled={selectedStaff.length === 0 && !switchMode}
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          </div>

                      {/* Right Panel - Staffing Boards */}
            <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg transition-colors duration-200 overflow-x-hidden h-full`}>
                          <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex">
                {['MOD'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setSelectedBoardTab(tab)}
                    className={`px-4 py-2 text-sm font-medium ${
                      selectedBoardTab === tab
                        ? 'text-blue-600 border-b-2 border-blue-500'
                        : `${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="p-4">

              {/* West Spider */}
              <div className="mb-4">
                <h3 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {getAreaDisplayName('westSideWaterspider', selectedFloor)} {currentFloorAssignedStaff.westSideWaterspider.length}/1
                </h3>
                <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} transition-colors duration-200`}>
                  <div className={`px-3 py-2 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} transition-colors duration-200`}>
                    <div className={`grid grid-cols-2 gap-4 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>
                      <span>Login</span>
                      <div className="flex items-center space-x-1">
                        <span>Role Lock</span>
                        <svg className="w-3 h-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`overflow-y-auto ${(() => {
                    const staffCount = currentFloorAssignedStaff.westSideWaterspider.length;
                    const rowHeight = 48; // Approximate height of each row (px)
                    const minHeight = 48; // Minimum height for one row (px)
                    const maxHeight = 200; // Maximum height (px)
                    
                    const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, staffCount > 0 ? (staffCount * rowHeight) : 48));
                    return `h-[${calculatedHeight}px]`;
                  })()}`}>
                    {currentFloorAssignedStaff.westSideWaterspider.length > 0 ? (
                      currentFloorAssignedStaff.westSideWaterspider.map((staff, index) => (
                        <div 
                          key={index} 
                          className={`px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${
                            switchMode && switchModeStaff && switchModeStaff.login === staff.login ? (isDarkMode ? 'bg-blue-900' : 'bg-blue-100') : ''
                          }`}
                          onClick={() => {
                            // Check if role is locked before allowing switch mode
                            if (roleLocks[staff.login]) {
                              alert(`Cannot switch ${staff.login}'s role - Role is locked. Click the lock icon in the staff list to unlock.`);
                              return;
                            }
                            handleStaffSelect(staff);
                          }}
                        >
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                              </svg>
                              <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{staff.login}</span>
                              {/* FCLM Redirect Button */}
                              <div className="relative group">
                                <button
                                  className={`p-1 rounded hover:bg-gray-200 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // FCLM redirect functionality
                                    handleFCLMRedirect(staff.login);
                                  }}
                                >
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </button>
                                {/* Tooltip */}
                                <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap ${
                                  isDarkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-gray-900 text-white'
                                }`}>
                                  Open in FCLM
                                  <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                                    isDarkMode ? 'border-t-gray-800' : 'border-t-gray-900'
                                  }`}></div>
                                </div>
                              </div>
                              <div className={`w-2 h-2 rounded-full ${
                                staff.status === 'red' ? 'bg-red-500' : 
                                staff.status === 'orange' ? 'bg-orange-500' :
                                staff.status === 'green' ? 'bg-green-500' : 'bg-green-300'
                              }`}></div>
                            </div>
                            <div className="flex items-center justify-between">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleRoleLock(staff.id, staff.login);
                                }}
                                className={`text-sm ${
                                  roleLocks[staff.login] 
                                    ? 'text-green-600 hover:text-green-800' 
                                    : 'text-gray-400 hover:text-gray-600'
                                }`}
                                title={roleLocks[staff.login] ? 'Role Locked - Click to unlock' : 'Role Unlocked - Click to lock'}
                              >
                                {roleLocks[staff.login] ? (
                                  <div className="flex items-center space-x-1">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-xs">Locked</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                    </svg>
                                    <span className="text-xs">Unlocked</span>
                                  </div>
                                )}
                              </button>

                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className={`px-3 py-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>--</div>
                    )}
                  </div>
                </div>
              </div>

              {/* West Tote Runner */}
              <div className="mb-4">
                <h3 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {getAreaDisplayName('westToteRunner', selectedFloor)} {currentFloorAssignedStaff.westToteRunner.length}/1
                </h3>
                <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} transition-colors duration-200`}>
                  <div className={`px-3 py-2 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} transition-colors duration-200`}>
                    <div className={`grid grid-cols-2 gap-4 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>
                      <span>Login</span>
                      <span>Role Lock</span>
                    </div>
                  </div>
                  <div className={`overflow-y-auto ${(() => {
                    const staffCount = currentFloorAssignedStaff.westToteRunner.length;
                    const rowHeight = 48; // Approximate height of each row (px)
                    const minHeight = 48; // Minimum height for one row (px)
                    const maxHeight = 200; // Maximum height (px)
                    
                    const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, staffCount > 0 ? (staffCount * rowHeight) : 48));
                    return `h-[${calculatedHeight}px]`;
                  })()}`}>
                    {currentFloorAssignedStaff.westToteRunner.length > 0 ? (
                      currentFloorAssignedStaff.westToteRunner.map((staff, index) => (
                        <div 
                          key={index} 
                          className={`px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${
                            switchMode && switchModeStaff && switchModeStaff.login === staff.login ? (isDarkMode ? 'bg-blue-900' : 'bg-blue-100') : ''
                          }`}
                          onClick={() => {
                            // Check if role is locked before allowing switch mode
                            if (roleLocks[staff.login]) {
                              alert(`Cannot switch ${staff.login}'s role - Role is locked. Click the lock icon in the staff list to unlock.`);
                              return;
                            }
                            handleStaffSelect(staff);
                          }}
                        >
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                              </svg>
                              <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{staff.login}</span>
                              {/* FCLM Redirect Button */}
                              <div className="relative group">
                                <button
                                  className={`p-1 rounded hover:bg-gray-200 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // FCLM redirect functionality
                                    handleFCLMRedirect(staff.login);
                                  }}
                                  title="Open in FCLM"
                                >
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </button>
                                {/* Tooltip */}
                                <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap ${
                                  isDarkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-gray-900 text-white'
                                }`}>
                                  Open in FCLM
                                  <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                                    isDarkMode ? 'border-t-gray-800' : 'border-t-gray-900'
                                  }`}></div>
                                </div>
                              </div>
                              <div className={`w-2 h-2 rounded-full ${
                                staff.status === 'red' ? 'bg-red-500' : 
                                staff.status === 'orange' ? 'bg-orange-500' :
                                staff.status === 'green' ? 'bg-green-500' : 'bg-green-300'
                              }`}></div>
                            </div>
                            <div className="flex items-center justify-between">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleRoleLock(staff.id, staff.login);
                                }}
                                className={`text-sm ${
                                  roleLocks[staff.login] 
                                    ? 'text-green-600 hover:text-green-800' 
                                    : 'text-gray-400 hover:text-gray-600'
                                }`}
                                title={roleLocks[staff.login] ? 'Role Locked - Click to unlock' : 'Role Unlocked - Click to lock'}
                              >
                                {roleLocks[staff.login] ? (
                                  <div className="flex items-center space-x-1">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-xs">Locked</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                    </svg>
                                    <span className="text-xs">Unlocked</span>
                                  </div>
                                )}
                              </button>

                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className={`px-3 py-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>--</div>
                    )}
                  </div>
                </div>
              </div>

              {/* West Jam Clear - A02 only */}
              {selectedFloor === 'A02' && (
                <div className="mb-4">
                  <h3 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {getAreaDisplayName('westJamClear', selectedFloor)} {(currentFloorAssignedStaff.westJamClear || []).length}/1
                  </h3>
                  <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} transition-colors duration-200`}>
                    <div className={`px-3 py-2 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} transition-colors duration-200`}>
                      <div className={`grid grid-cols-2 gap-4 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>
                        <span>Login</span>
                        <span>Role Lock</span>
                      </div>
                    </div>
                    <div className={`overflow-y-auto ${(() => {
                      const staffCount = (currentFloorAssignedStaff.westJamClear || []).length;
                      const rowHeight = 48; // Approximate height of each row (px)
                      const minHeight = 48; // Minimum height for one row (px)
                      const maxHeight = 200; // Maximum height (px)
                      
                      const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, staffCount > 0 ? (staffCount * rowHeight) : 48));
                      return `h-[${calculatedHeight}px]`;
                    })()}`}>
                      {(currentFloorAssignedStaff.westJamClear || []).length > 0 ? (
                        (currentFloorAssignedStaff.westJamClear || []).map((staff, index) => (
                          <div 
                            key={index} 
                            className={`px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${
                              switchMode && switchModeStaff && switchModeStaff.login === staff.login ? (isDarkMode ? 'bg-blue-900' : 'bg-blue-100') : ''
                            }`}
                            onClick={() => {
                              // Check if role is locked before allowing switch mode
                              if (roleLocks[staff.login]) {
                                alert(`Cannot switch ${staff.login}'s role - Role is locked. Click the lock icon in the staff list to unlock.`);
                                return;
                              }
                              handleStaffSelect(staff);
                            }}
                          >
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div className="flex items-center space-x-2">
                                <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                </svg>
                                <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{staff.login}</span>
                                {/* FCLM Redirect Button */}
                                <div className="relative group">
                                  <button
                                    className={`p-1 rounded hover:bg-gray-200 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // FCLM redirect functionality
                                      handleFCLMRedirect(staff.login);
                                    }}
                                  >
                                    <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </button>
                                  {/* Tooltip */}
                                  <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap ${
                                    isDarkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-gray-900 text-white'
                                  }`}>
                                    Open in FCLM
                                    <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                                      isDarkMode ? 'border-t-gray-800' : 'border-t-gray-900'
                                    }`}></div>
                                  </div>
                                </div>
                                <div className={`w-2 h-2 rounded-full ${
                                  staff.status === 'red' ? 'bg-red-500' : 
                                  staff.status === 'orange' ? 'bg-orange-500' :
                                  staff.status === 'green' ? 'bg-green-500' : 'bg-green-300'
                                }`}></div>
                              </div>
                              <div className="flex items-center justify-between">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleRoleLock(staff.id, staff.login);
                                      }}
                                      className={`text-sm ${
                                        roleLocks[staff.login] 
                                          ? 'text-green-600 hover:text-green-800' 
                                          : 'text-gray-400 hover:text-gray-600'
                                      }`}
                                      title={roleLocks[staff.login] ? 'Role Locked - Click to unlock' : 'Role Unlocked - Click to lock'}
                                    >
                                      {roleLocks[staff.login] ? (
                                        <div className="flex items-center space-x-1">
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                          </svg>
                                          <span className="text-xs">Locked</span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center space-x-1">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                          </svg>
                                          <span className="text-xs">Unlocked</span>
                                        </div>
                                      )}
                                    </button>
                                    
                                  </div>
                                </div>
                              </div>
                            ))
                      ) : (
                        <div className={`px-3 py-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>--</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* West PA */}
              <div>
                <h3 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  West PA {currentFloorAssignedStaff.westPA.length}/1
                </h3>
                <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} transition-colors duration-200`}>
                  <div className={`px-3 py-2 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} transition-colors duration-200`}>
                    <div className={`grid grid-cols-2 gap-4 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>
                      <span>Login</span>
                      <span>Role Lock</span>
                    </div>
                  </div>
                  
                  <div className={`overflow-y-auto ${(() => {
                    const staffCount = currentFloorAssignedStaff.westPA.length;
                    const rowHeight = 48; // Approximate height of each row (px)
                    const minHeight = 48; // Minimum height for one row (px)
                    const maxHeight = 200; // Maximum height (px)
                    
                    const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, staffCount > 0 ? (staffCount * rowHeight) : 48));
                    return `h-[${calculatedHeight}px]`;
                  })()}`}>
                    {currentFloorAssignedStaff.westPA.length > 0 ? (
                      currentFloorAssignedStaff.westPA.map((staff, index) => (
                        <div 
                          key={index} 
                          className={`px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${
                            switchMode && switchModeStaff && switchModeStaff.login === staff.login ? (isDarkMode ? 'bg-blue-900' : 'bg-blue-100') : ''
                          }`}
                          onClick={() => {
                            // Check if role is locked before allowing switch mode
                            if (roleLocks[staff.login]) {
                              alert(`Cannot switch ${staff.login}'s role - Role is locked. Click the lock icon in the staff list to unlock.`);
                              return;
                            }
                            handleStaffSelect(staff);
                          }}
                        >
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                              </svg>
                              <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{staff.login}</span>
                              {/* FCLM Redirect Button */}
                              <div className="relative group">
                                <button
                                  className={`p-1 rounded hover:bg-gray-200 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // FCLM redirect functionality
                                    handleFCLMRedirect(staff.login);
                                  }}
                                  title="Open in FCLM"
                                >
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </button>
                                {/* Tooltip */}
                                <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap ${
                                  isDarkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-gray-900 text-white'
                                }`}>
                                  Open in FCLM
                                  <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                                    isDarkMode ? 'border-t-gray-800' : 'border-t-gray-900'
                                  }`}></div>
                                </div>
                              </div>
                              <div className={`w-2 h-2 rounded-full ${
                                staff.status === 'red' ? 'bg-red-500' : 
                                staff.status === 'orange' ? 'bg-orange-500' :
                                staff.status === 'green' ? 'bg-green-500' : 'bg-green-300'
                              }`}></div>
                            </div>
                            <div className="flex items-center justify-between">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleRoleLock(staff.id, staff.login);
                                }}
                                className={`text-sm ${
                                  roleLocks[staff.login] 
                                    ? 'text-green-600 hover:text-green-800' 
                                    : 'text-gray-400 hover:text-gray-600'
                                }`}
                                title={roleLocks[staff.login] ? 'Role Locked - Click to unlock' : 'Role Unlocked - Click to lock'}
                              >
                                {roleLocks[staff.login] ? (
                                  <div className="flex items-center space-x-1">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-xs">Locked</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                    </svg>
                                    <span className="text-xs">Unlocked</span>
                                  </div>
                                )}
                              </button>

                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className={`px-3 py-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>--</div>
                    )}
                  </div>
                </div>
              </div>

              {/* East Spider */}
              <div className="mb-4">
                <h3 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  East Spider {currentFloorAssignedStaff.eastSpider.length}/1
                </h3>
                <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} transition-colors duration-200`}>
                  <div className={`px-3 py-2 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} transition-colors duration-200`}>
                    <div className={`grid grid-cols-2 gap-4 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>
                      <span>Login</span>
                      <div className="flex items-center space-x-1">
                        <span>Role Lock</span>
                        <svg className="w-3 h-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`overflow-y-auto ${(() => {
                    const staffCount = currentFloorAssignedStaff.eastSpider.length;
                    const rowHeight = 48; // Approximate height of each row (px)
                    const minHeight = 48; // Minimum height for one row (px)
                    const maxHeight = 200; // Maximum height (px)
                    
                    const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, staffCount > 0 ? (staffCount * rowHeight) : 48));
                    return `h-[${calculatedHeight}px]`;
                  })()}`}>
                    {currentFloorAssignedStaff.eastSpider.length > 0 ? (
                      currentFloorAssignedStaff.eastSpider.map((staff, index) => (
                        <div 
                          key={index} 
                          className={`px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${
                            switchMode && switchModeStaff && switchModeStaff.login === staff.login ? (isDarkMode ? 'bg-blue-900' : 'bg-blue-100') : ''
                          }`}
                          onClick={() => {
                            // Check if role is locked before allowing switch mode
                            if (roleLocks[staff.login]) {
                              alert(`Cannot switch ${staff.login}'s role - Role is locked. Click the lock icon in the staff list to unlock.`);
                              return;
                            }
                            handleStaffSelect(staff);
                          }}
                        >
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                              </svg>
                              <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{staff.login}</span>
                              {/* FCLM Redirect Button */}
                              <div className="relative group">
                                <button
                                  className={`p-1 rounded hover:bg-gray-200 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // FCLM redirect functionality
                                    handleFCLMRedirect(staff.login);
                                  }}
                                  title="Open in FCLM"
                                >
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </button>
                                {/* Tooltip */}
                                <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap ${
                                  isDarkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-gray-900 text-white'
                                }`}>
                                  Open in FCLM
                                  <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                                    isDarkMode ? 'border-t-gray-800' : 'border-t-gray-900'
                                  }`}></div>
                                </div>
                              </div>
                              <div className={`w-2 h-2 rounded-full ${
                                staff.status === 'red' ? 'bg-red-500' : 
                                staff.status === 'orange' ? 'bg-orange-500' :
                                staff.status === 'green' ? 'bg-green-500' : 'bg-green-300'
                              }`}></div>
                            </div>
                            <div className="flex items-center justify-between">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleRoleLock(staff.id, staff.login);
                                }}
                                className={`text-sm ${
                                  roleLocks[staff.login] 
                                    ? 'text-green-600 hover:text-green-800' 
                                    : 'text-gray-400 hover:text-gray-600'
                                }`}
                                title={roleLocks[staff.login] ? 'Role Locked - Click to unlock' : 'Role Unlocked - Click to lock'}
                              >
                                {roleLocks[staff.login] ? (
                                  <div className="flex items-center space-x-1">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-xs">Locked</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                    </svg>
                                    <span className="text-xs">Unlocked</span>
                                  </div>
                                )}
                              </button>
                              
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className={`px-3 py-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>--</div>
                    )}
                  </div>
                </div>
              </div>

              

              {/* A01-specific sections */}
              {selectedFloor === 'A01' && (
                <>
                  {/* East Runner */}
                  <div className="mb-4">
                    <h3 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {getAreaDisplayName('eastRunner', selectedFloor)} {currentFloorAssignedStaff.eastRunner.length}/1
                    </h3>
                    <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} transition-colors duration-200`}>
                      <div className={`px-3 py-2 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} transition-colors duration-200`}>
                        <div className={`grid grid-cols-2 gap-4 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>
                          <span>Login</span>
                          <span>Role Lock</span>
                        </div>
                      </div>
                                            <div className={`overflow-y-auto ${(() => {
                        const staffCount = currentFloorAssignedStaff.eastRunner.length;
                        const rowHeight = 48; // Approximate height of each row (px)
                        const minHeight = 48; // Minimum height for one row (px)
                        const maxHeight = 200; // Maximum height (px)
                        
                        const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, staffCount > 0 ? (staffCount * rowHeight) : 48));
                        return `h-[${calculatedHeight}px]`;
                      })()}`}>
                                                {currentFloorAssignedStaff.eastRunner.length > 0 ? (
                          currentFloorAssignedStaff.eastRunner.map((staff, index) => (
                            <div 
                              key={index} 
                              className={`px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${
                              switchMode && switchModeStaff && switchModeStaff.login === staff.login ? (isDarkMode ? 'bg-blue-900' : 'bg-blue-100') : ''
                            }`}
                          onClick={() => {
                            // Check if role is locked before allowing switch mode
                            if (roleLocks[staff.login]) {
                              alert(`Cannot switch ${staff.login}'s role - Role is locked. Click the lock icon in the staff list to unlock.`);
                              return;
                            }
                            handleStaffSelect(staff);
                          }}
                        >
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                              </svg>
                              <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{staff.login}</span>
                              {/* FCLM Redirect Button */}
                              <div className="relative group">
                                <button
                                  className={`p-1 rounded hover:bg-gray-200 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // FCLM redirect functionality
                                    handleFCLMRedirect(staff.login);
                                  }}
                                  title="Open in FCLM"
                                >
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </button>
                                {/* Tooltip */}
                                <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap ${
                                  isDarkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-gray-900 text-white'
                                }`}>
                                  Open in FCLM
                                  <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                                    isDarkMode ? 'border-t-gray-800' : 'border-t-gray-900'
                                  }`}></div>
                                </div>
                              </div>
                              <div className={`w-2 h-2 rounded-full ${
                                staff.status === 'red' ? 'bg-red-500' : 
                                staff.status === 'orange' ? 'bg-orange-500' :
                                staff.status === 'green' ? 'bg-green-500' : 'bg-green-300'
                              }`}></div>
                            </div>
                            <div className="flex items-center justify-between">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleRoleLock(staff.id, staff.login);
                                    }}
                                    className={`text-sm ${
                                      roleLocks[staff.login] 
                                        ? 'text-green-600 hover:text-green-800' 
                                        : 'text-gray-400 hover:text-gray-600'
                                    }`}
                                    title={roleLocks[staff.login] ? 'Role Locked - Click to unlock' : 'Role Unlocked - Click to lock'}
                                  >
                                    {roleLocks[staff.login] ? (
                                      <div className="flex items-center space-x-1">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-xs">Locked</span>
                                      </div>
                                    ) : (
                                      <div className="flex items-center space-x-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                        </svg>
                                        <span className="text-xs">Unlocked</span>
                                      </div>
                                    )}
                                  </button>
                                  
                                </div>
                              </div>
                            </div>
                          ))
                                            ) : (
                      <div className={`px-3 py-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>--</div>
                    )}
                  </div>
                </div>
              </div>

                                {/* East VRC */}
                  <div className="mb-4">
                    <h3 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {getAreaDisplayName('eastVRC', selectedFloor)} {currentFloorAssignedStaff.eastVRC.length}/1
                    </h3>
                    <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} transition-colors duration-200`}>
                      <div className={`px-3 py-2 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} transition-colors duration-200`}>
                        <div className={`grid grid-cols-2 gap-4 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>
                          <span>Login</span>
                          <span>Role Lock</span>
                        </div>
                      </div>
                      <div className={`overflow-y-auto ${(() => {
                        const staffCount = currentFloorAssignedStaff.eastVRC.length;
                        const rowHeight = 48; // Approximate height of each row (px)
                        const minHeight = 48; // Minimum height for one row (px)
                        const maxHeight = 200; // Maximum height (px)
                        
                        const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, staffCount > 0 ? (staffCount * rowHeight) : 48));
                        return `h-[${calculatedHeight}px]`;
                      })()}`}>
                        {currentFloorAssignedStaff.eastVRC.length > 0 ? (
                          currentFloorAssignedStaff.eastVRC.map((staff, index) => (
                            <div 
                              key={index} 
                              className={`px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${
                                switchMode && switchModeStaff && switchModeStaff.login === staff.login ? (isDarkMode ? 'bg-blue-900' : 'bg-blue-100') : ''
                              }`}
                              onClick={() => {
                                // Check if role is locked before allowing switch mode
                                if (roleLocks[staff.login]) {
                                  alert(`Cannot switch ${staff.login}'s role - Role is locked. Click the lock icon in the staff list to unlock.`);
                                  return;
                                }
                                handleStaffSelect(staff);
                              }}
                            >
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="flex items-center space-x-2">
                                  <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                  </svg>
                                  <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{staff.login}</span>
                                  {/* FCLM Redirect Button */}
                                  <div className="relative group">
                                    <button
                                      className={`p-1 rounded hover:bg-gray-200 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // FCLM redirect functionality
                                        handleFCLMRedirect(staff.login);
                                      }}
                                      title="Open in FCLM"
                                    >
                                      <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                      </svg>
                                    </button>
                                    {/* Tooltip */}
                                    <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap ${
                                      isDarkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-gray-900 text-white'
                                    }`}>
                                      Open in FCLM
                                      <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                                        isDarkMode ? 'border-t-gray-800' : 'border-t-gray-900'
                                      }`}></div>
                                    </div>
                                  </div>
                                  <div className={`w-2 h-2 rounded-full ${
                                    staff.status === 'red' ? 'bg-red-500' : 
                                    staff.status === 'orange' ? 'bg-orange-500' :
                                    staff.status === 'green' ? 'bg-green-500' : 'bg-green-300'
                                  }`}></div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleRoleLock(staff.id, staff.login);
                                    }}
                                    className={`text-sm ${
                                      roleLocks[staff.login] 
                                        ? 'text-green-600 hover:text-green-800' 
                                        : 'text-gray-400 hover:text-gray-600'
                                    }`}
                                    title={roleLocks[staff.login] ? 'Role Locked - Click to unlock' : 'Role Unlocked - Click to lock'}
                                  >
                                    {roleLocks[staff.login] ? (
                                      <div className="flex items-center space-x-1">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-xs">Locked</span>
                                      </div>
                                    ) : (
                                      <div className="flex items-center space-x-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                        </svg>
                                        <span className="text-xs">Unlocked</span>
                                      </div>
                                    )}
                                  </button>
                                  
                                </div>
                              </div>
                            </div>
                          ))
                                            ) : (
                      <div className={`px-3 py-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>--</div>
                    )}
                  </div>
                </div>
              </div>


            </>
          )}



              {/* East PA */}
              <div className="mb-4">
                <h3 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {getAreaDisplayName('eastPA', selectedFloor)} {currentFloorAssignedStaff.eastPA.length}/1
                </h3>
                <div className={`border rounded ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} transition-colors duration-200`}>
                  <div className={`px-3 py-2 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} transition-colors duration-200`}>
                    <div className={`grid grid-cols-2 gap-4 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>
                      <span>Login</span>
                      <span>Role Lock</span>
                    </div>
                  </div>
                  <div className={`overflow-y-auto ${(() => {
                    const staffCount = currentFloorAssignedStaff.eastPA.length;
                    const rowHeight = 48; // Approximate height of each row (px)
                    const minHeight = 48; // Minimum height for one row (px)
                    const maxHeight = 200; // Maximum height (px)
                    
                    const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, staffCount > 0 ? (staffCount * rowHeight) : 48));
                    return `h-[${calculatedHeight}px]`;
                  })()}`}>
                    {currentFloorAssignedStaff.eastPA.length > 0 ? (
                      currentFloorAssignedStaff.eastPA.map((staff, index) => (
                        <div 
                          key={index} 
                          className={`px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${
                            switchMode && switchModeStaff && switchModeStaff.login === staff.login ? (isDarkMode ? 'bg-blue-900' : 'bg-blue-100') : ''
                          }`}
                          onClick={() => {
                            // Check if role is locked before allowing switch mode
                            if (roleLocks[staff.login]) {
                              alert(`Cannot switch ${staff.login}'s role - Role is locked. Click the lock icon in the staff list to unlock.`);
                              return;
                            }
                            handleStaffSelect(staff);
                          }}
                        >
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                              </svg>
                              <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{staff.login}</span>
                              {/* FCLM Redirect Button */}
                              <div className="relative group">
                                <button
                                  className={`p-1 rounded hover:bg-gray-200 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // FCLM redirect functionality
                                    handleFCLMRedirect(staff.login);
                                  }}
                                  title="Open in FCLM"
                                >
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </button>
                                {/* Tooltip */}
                                <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 ${
                                  isDarkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-gray-900 text-white'
                                }`}>
                                  Open in FCLM
                                  <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                                    isDarkMode ? 'border-t-gray-800' : 'border-t-gray-900'
                                  }`}></div>
                                </div>
                              </div>
                              <div className={`w-2 h-2 rounded-full ${
                                staff.status === 'red' ? 'bg-red-500' : 
                                staff.status === 'orange' ? 'bg-orange-500' :
                                staff.status === 'green' ? 'bg-green-500' : 'bg-green-300'
                              }`}></div>
                            </div>
                            <div className="flex items-center justify-between">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleRoleLock(staff.id, staff.login);
                                    }}
                                    className={`text-sm ${
                                      roleLocks[staff.login] 
                                        ? 'text-green-600 hover:text-green-800' 
                                        : 'text-gray-400 hover:text-gray-600'
                                    }`}
                                    title={roleLocks[staff.login] ? 'Role Locked - Click to unlock' : 'Role Unlocked - Click to lock'}
                                  >
                                    {roleLocks[staff.login] ? (
                                      <div className="flex items-center space-x-1">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-xs">Locked</span>
                                      </div>
                                    ) : (
                                      <div className="flex items-center space-x-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                        </svg>
                                        <span className="text-xs">Unlocked</span>
                                      </div>
                                    )}
                                  </button>
                                  
                                </div>
                              </div>
                            </div>
                          ))
                    ) : (
                      <div className={`px-3 py-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>--</div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
        )}

                  {/* Bottom Bar */}
          <div className={`fixed bottom-0 left-0 right-0 px-4 py-2 transition-colors duration-200 ${isDarkMode ? 'bg-gray-800 border-t border-gray-700' : 'bg-white border-t border-gray-200'}`}>
          <div className="grid grid-cols-3 gap-4 items-center">
            {/* Left Side - Dark Mode Toggle and Dev Mode Exit */}
            <div className="flex space-x-2">
              {/* Exit Dev Mode Button - Only show when in dev mode */}
              {isDevMode && (
                <button
                  onClick={onExitDevMode}
                  className={`p-2 rounded-lg transition-colors duration-200 ${
                    isDarkMode 
                      ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                      : 'bg-red-50 text-red-600 hover:bg-red-100'
                  }`}
                  title="Exit Developer Mode"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              )}
              
              {/* Dark Mode Toggle Button */}
              <button
              onClick={toggleDarkMode}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                isDarkMode 
                  ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDarkMode ? (
                // Sun icon for dark mode
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                // Moon icon for light mode
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
            
            {/* Monitor Button - moved from header */}
            <button
              onClick={() => window.open('https://staff.carrte.dev/health.html', '_blank', 'noopener,noreferrer')}
              className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-medium border transition
                ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}
                ${monitorStatus === 'operational' ? 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200' :
                   monitorStatus === 'degraded' ? 'bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-200' :
                   monitorStatus === 'error' ? 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200' :
                   'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200'}`}
              title="Open Live Health Monitor"
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M2 10a8 8 0 1116 0A8 8 0 012 10zm4.5-1a1 1 0 100 2h1.75l.72 2.16a1 1 0 001.9 0L12.6 9h.9a1 1 0 100-2h-1.6a1 1 0 00-.95.68L9.9 9.2 9.2 7.05A1 1 0 008.25 6.4H6.5z" />
              </svg>
              <span>
                {monitorStatus === 'operational' ? 'all systems normal' :
                 monitorStatus === 'degraded' ? 'systems degraded' :
                 monitorStatus === 'error' ? 'systems down' :
                 'checking systems...'}
              </span>
            </button>
            
            {/* Attribution Link - Left Side */}
            <div className="flex items-center ml-4">
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Built and maintained by{' '}
                <a
                  href="https://phonetool.amazon.com/users/acarreom"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`hover:underline transition-colors ${
                    isDarkMode 
                      ? 'text-blue-400 hover:text-blue-300' 
                      : 'text-blue-600 hover:text-blue-800'
                  }`}
                >
                  Angel Carreon (acarreom)
                </a>
              </span>
            </div>
            </div>
            
            {/* Center - Broadcast Controls */}
            <div className="flex justify-center items-center">
              {selectedTab === 'Approved' && (
                <div className="flex items-center space-x-3">
                  {/* Broadcast Status */}
                  <div className={`flex items-center space-x-2 px-2 py-1 rounded text-xs ${
                    broadcastStatus === 'broadcasting' 
                      ? 'bg-green-100 text-green-800 border border-green-300' 
                      : broadcastStatus === 'error'
                        ? 'bg-red-100 text-red-800 border border-red-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-300'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${
                      broadcastStatus === 'broadcasting' 
                        ? 'bg-green-500' 
                        : broadcastStatus === 'error'
                          ? 'bg-red-500'
                          : 'bg-gray-400'
                    }`}></div>
                    <span className="font-medium">
                      {broadcastStatus === 'broadcasting' 
                        ? `Broadcasting: ${broadcastPlan?.name?.split(' - ')[0] || 'Plan'}`
                        : broadcastStatus === 'error'
                          ? 'Broadcast Error'
                          : 'No Active Broadcast'
                      }
                    </span>
                    {broadcastStatus === 'broadcasting' && (
                      <button
                        onClick={clearBroadcast}
                        className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                        title="Stop broadcasting"
                      >
                        Stop
                      </button>
                    )}
                  </div>
                  
                  {/* Broadcast Help */}
                  <div className="text-xs text-gray-500">
                    💡 <a 
                      href="/plan-fullscreen" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Open broadcast view
                    </a>
                  </div>
                </div>
              )}
            </div>
            
            {/* Right Side - Action Buttons */}
            <div className="flex justify-end space-x-2">
              <button className="bg-blue-500 text-white px-4 py-1 rounded text-sm">Message</button>
              <button 
                onClick={() => {
                  if (switchMode && switchModeStaff) {
                    // Find which area the staff is currently assigned to
                    for (const area of Object.keys(assignedStaff[selectedFloor])) {
                      if (assignedStaff[selectedFloor][area].find(staff => staff.login === switchModeStaff.login)) {
                        handleUnassignStaff(switchModeStaff, area);
                        // Clear switch mode after unassigning
                        setSwitchMode(false);
                        setSwitchModeStaff(null);
                        setSelectedStaff([]);
                        break;
                      }
                    }
                  }
                }}
                className={`px-4 py-1 rounded text-sm ${
                  switchMode && switchModeStaff 
                    ? 'bg-red-500 text-white hover:bg-red-600' 
                    : 'bg-gray-300 text-gray-600'
                }`}
                disabled={!switchMode || !switchModeStaff}
              >
                Unassign
              </button>
              {switchMode && switchModeStaff && (
                <button 
                  onClick={() => {
                    const area = document.getElementById('assignArea').value;
                    if (area) {
                      handleRestaff(area);
                    } else {
                      alert('Please select an area to restaff staff to');
                    }
                  }}
                  className="bg-blue-500 text-white px-4 py-1 rounded text-sm hover:bg-blue-600"
                >
                  Switch
                </button>
              )}
              <div className="flex items-center space-x-2">
                <select 
                  id="assignArea"
                  className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                  defaultValue=""
                >
                  <option value="" disabled>
                    {switchMode ? 'Select New Area' : 'Select Area'}
                  </option>
                  <option value="westSideWaterspider">West Spider</option>
                  <option value="westToteRunner">{getAreaDisplayName('westToteRunner', selectedFloor)}</option>
                  {selectedFloor === 'A02' && (
                    <option value="westJamClear">West Jam Clear</option>
                  )}
                  <option value="westPA">West PA</option>
                  <option value="eastSpider">East Spider</option>
                                    <option value="eastPA">{getAreaDisplayName('eastPA', selectedFloor)}</option>
                  {selectedFloor === 'A01' && (
                    <>
                      <option value="eastRunner">{getAreaDisplayName('eastRunner', selectedFloor)}</option>
                      <option value="eastVRC">{getAreaDisplayName('eastVRC', selectedFloor)}</option>
                    </>
                  )}
                </select>
                {!switchMode && (
                  <button 
                    onClick={() => {
                      const area = document.getElementById('assignArea').value;
                      if (area) {
                        handleAssignStaff(area);
                      } else {
                        alert('Please select an area to assign staff to');
                      }
                    }}
                    className={`px-4 py-1 rounded text-sm ${
                      selectedStaff.length > 0 
                        ? 'bg-green-500 text-white hover:bg-green-600' 
                        : 'bg-gray-300 text-gray-600 disabled:opacity-50'
                    }`}
                    disabled={selectedStaff.length === 0}
                  >
                    Assign
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      {showLoginForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 w-96 max-w-md transition-colors duration-200`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Login to Staffing Board</h2>
              <button
                onClick={() => setShowLoginForm(false)}
                className={`text-xl ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-400 hover:text-gray-600'}`}
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Email Address
                </label>
                <input
                  type="email"
                  id="loginEmail"
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                  placeholder="Enter your email address"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const email = document.getElementById('loginEmail').value.trim();
                      const password = document.getElementById('loginPassword').value.trim();
                      if (email && password) {
                        handleLogin(email, password);
                      }
                    }
                  }}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Password
                </label>
                <input
                  type="password"
                  id="loginPassword"
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                  placeholder="Enter password (required for staff editing)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const email = document.getElementById('loginEmail').value.trim();
                      const password = document.getElementById('loginPassword').value.trim();
                      if (email && password) {
                        handleLogin(email, password);
                      }
                    }
                  }}
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowLoginForm(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded text-sm hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const email = document.getElementById('loginEmail').value.trim();
                    const password = document.getElementById('loginPassword').value.trim();
                    if (email && password) {
                      handleLogin(email, password);
                    } else {
                      alert('Please enter both email address and password');
                    }
                  }}
                  className="bg-blue-500 text-white px-4 py-2 rounded text-sm hover:bg-blue-600"
                >
                  Login
                </button>
              </div>
              
              <div className={`text-xs text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Login is optional and not required to create a staffing plan.<br/>
                Password is required for staff editing privileges.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Picker Modal for Plan Creation */}
      {showDatePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 w-96 max-w-md transition-colors duration-200`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Create Staffing Plan</h2>
              <button
                onClick={handleCancelSavePlan}
                className={`text-xl ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-400 hover:text-gray-600'}`}
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Date for this plan
                </label>
                <input
                  type="text"
                  value={planDateInput}
                  onChange={(e) => setPlanDateInput(e.target.value)}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                  placeholder="M/D/YYYY or MM/DD/YYYY (e.g., 8/9/2025)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleConfirmSavePlan();
                    }
                  }}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Enter the date this staffing plan is for (flexible format: 8/9/2025 or 08/09/2025)
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>
                  ⚠️ Plans automatically expire and are deleted after 24 hours
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Shift
                </label>
                <select
                  value={selectedShift}
                  onChange={(e) => setSelectedShift(e.target.value)}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                >
                  <option value="FHD">FHD (Front Half Day)</option>
                  <option value="FHN">FHN (Front Half Night)</option>
                  <option value="BHD">BHD (Back Half Day)</option>
                  <option value="RT">RT (Reduced Time)</option>
                </select>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Select the shift this staffing plan is for
                </p>
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  onClick={handleCancelSavePlan}
                  className="bg-gray-500 text-white px-4 py-2 rounded text-sm hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSavePlan}
                  className="bg-green-500 text-white px-4 py-2 rounded text-sm hover:bg-green-600"
                >
                  Create Plan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;